// CR075-P2: Batch Run Steps Panel with S1 Flyers + S2 Website + S3 Forum support
// CR075-P5: Added PARTIAL status + Retry functionality + attempt-scoped reconciliation
// CR075-P6: Added NEEDS_ACTION status + "Render All + Continue" orchestration
// CR080: Added FlyerPipelineStepsPanel for candidate-first workflow
// CR121: Added persisted Auto Mode per step group with cross-group chaining
// CR: Added validated payload unwrapping utilities for proper field access
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Play, Clock, CheckCircle2, XCircle, AlertCircle, Loader2, ChevronDown, Eye, Download,
  FileText, Globe, RefreshCw, MessageSquare, ExternalLink, RotateCcw, ImageIcon, FastForward, Square, StopCircle, Trash2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { RetryStepDialog } from "./RetryStepDialog";
import { S1ExtractDiagnosticsPanel } from "./S1ExtractDiagnosticsPanel";
import { S5WorkflowPackSelector } from "./S5WorkflowPackSelector";
import { RenderAllAndContinueDialog } from "./RenderAllAndContinueDialog";
import { S0ScanSettingsAccordion } from "./S0ScanSettingsAccordion";
import { FlyerPipelineStepsPanel } from "./FlyerPipelineStepsPanel";
import { StepGroupHeader } from "./StepGroupHeader";
import { Phase4PublishSteps } from "./Phase4PublishSteps";
import { S05CollectionStepTile } from "./S05CollectionStepTile";
import { StepErrorDetails } from "./StepErrorDetails";
import { S2WebScanDebugPanel } from "./S2WebScanDebugPanel";
import { S4AdvancedDiagnostics } from "./S4AdvancedDiagnostics";
import { useBatchRunAutoMode } from "@/hooks/useBatchRunAutoMode";
import { useLatestS2Attempt, deriveEffectiveS2Status } from "@/hooks/useLatestS2Attempt";
import { getDealType } from "@/lib/batch-runs/validatedPayloadUtils";
import {
  StepGroupKey,
  STEP_GROUP_SEQUENCE,
  getPhase1RunChainSequence,
} from "@/lib/batch-runs/autoModeTypes";
import {
  PipelineDisplayStatus,
  getPipelineDisplayStatus,
  getPipelineStatusHelperText,
} from "@/lib/batch-runs/pipelineDisplayStatus";
import { getEffectiveAttemptStatus } from "@/lib/batch-runs/attemptStatus";
import {
  advanceSnapshotStability,
  createInitialSnapshotStabilityState,
  type SnapshotStabilityPhase,
} from "@/lib/batch-runs/snapshotStability";
import { isS1ReadyForDownstream, shouldBlockS4ForSnapshotSettling } from "@/lib/batch-runs/s1StepState";
import { selectCanonicalS1Snapshot } from "@/lib/batch-runs/s1SnapshotSelection";
import { resolveFlyerCaptureMode } from "@/lib/batch-runs/flyerCaptureMode";
import { mapS05DownloadRunStatus, type S05StepStatus } from "@/lib/batch-runs/s05Status";

interface BatchRunStep {
  id: string;
  batch_run_id: string;
  step_key: string;
  step_name: string;
  status: string;
  mode: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  records_in: number;
  records_out: number;
  skipped_reason: string | null;
  error_message: string | null;
  config_json: Record<string, unknown>;
  units_total?: number;
  units_succeeded?: number;
  units_failed?: number;
  attempt_count?: number;
}

interface BatchRunStepsPanelProps {
  batchRunId: string;
  steps: BatchRunStep[];
  runStatus: string;
  sourcesEnabled?: string[];
  currentConfig?: Record<string, unknown>;
  onStepExecuted: () => void;
  onViewOutput?: (stepKey: string) => void;
  /** If true, step mode is controlled by scheduler and toggle should be disabled */
  isSchedulerRun?: boolean;
}

const stepStatusConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  NOT_STARTED: { label: "Not Started", icon: <Clock className="h-4 w-4" />, color: "text-muted-foreground" },
  PENDING: { label: "Pending", icon: <Clock className="h-4 w-4" />, color: "text-muted-foreground" },
  RUNNING: { label: "Running", icon: <Loader2 className="h-4 w-4 animate-spin" />, color: "text-blue-500" },
  COMPLETE: { label: "Complete", icon: <CheckCircle2 className="h-4 w-4" />, color: "text-green-500" },
  BLOCKED: { label: "Blocked", icon: <AlertCircle className="h-4 w-4" />, color: "text-amber-500" },
  SETTLING: { label: "Settling", icon: <Clock className="h-4 w-4" />, color: "text-amber-500" },
  PARTIAL: { label: "Partial", icon: <AlertCircle className="h-4 w-4" />, color: "text-yellow-500" },
  NEEDS_ACTION: { label: "Action Required", icon: <AlertCircle className="h-4 w-4" />, color: "text-amber-500" },
  FAILED: { label: "Failed", icon: <XCircle className="h-4 w-4" />, color: "text-destructive" },
  SKIPPED: { label: "Skipped", icon: <Clock className="h-4 w-4" />, color: "text-muted-foreground" },
};

function mapDisplayStatusToConfigKey(displayStatus: PipelineDisplayStatus, rawStatus: string): string {
  if (displayStatus === "FAILED") return "FAILED";
  if (displayStatus === "RUNNING") return "RUNNING";
  if (displayStatus === "BLOCKED") return "BLOCKED";
  if (displayStatus === "SETTLING") return "SETTLING";
  if (displayStatus === "COMPLETE") return rawStatus === "PARTIAL" ? "PARTIAL" : "COMPLETE";
  return rawStatus || "NOT_STARTED";
}

// CR075-AC: Helper to derive failure summary from failed_units array
// Returns a human-readable label based on failure reasons (e.g., "1 timed out" vs "2 failed")
function deriveFailureSummary(failedUnits: Array<{ failure_reason?: string; status?: string }> | undefined): {
  label: string;
  isTimeout: boolean;
  timedOutCount: number;
  hardFailCount: number;
} {
  if (!failedUnits || failedUnits.length === 0) {
    return { label: "", isTimeout: false, timedOutCount: 0, hardFailCount: 0 };
  }

  const timedOutCount = failedUnits.filter(u =>
    u.failure_reason === "TIMED_OUT" || u.status === "TIMED_OUT"
  ).length;
  const hardFailCount = failedUnits.length - timedOutCount;

  const parts: string[] = [];
  if (timedOutCount > 0) {
    parts.push(`${timedOutCount} timed out`);
  }
  if (hardFailCount > 0) {
    parts.push(`${hardFailCount} failed`);
  }

  return {
    label: parts.join(", "),
    isTimeout: timedOutCount > 0 && hardFailCount === 0,
    timedOutCount,
    hardFailCount,
  };
}

const stepIcons: Record<string, React.ReactNode> = {
  S1: <FileText className="h-4 w-4" />,
  "S0.5": <Download className="h-4 w-4" />,
  S1_EXTRACT: <FileText className="h-4 w-4" />,
  S2: <Globe className="h-4 w-4" />,
  S3: <MessageSquare className="h-4 w-4" />,
  S4: <RefreshCw className="h-4 w-4" />,
  S5: <RefreshCw className="h-4 w-4" />,
  S5_CROP: <ImageIcon className="h-4 w-4" />,
  S6: <Eye className="h-4 w-4" />,
  S7: <CheckCircle2 className="h-4 w-4" />,
};

// CR452: Use centralized step key normalization
import { getStepDisplayLabel } from "@/lib/batch-runs/stepKeyNormalization";

// CR434/CR485/CR502/CR452: stepDisplayKey now generated from centralized normalization
const stepDisplayKey: Record<string, string> = {
  S5_CROP: "S1.5",
  S1_SNIPPET: "S1.5",
  S1_4: "S1.5",
  S1_OCR: "S1.2",
  S1_AI: "S1.4",
  S1_RENDER: "S1.1",
  S1_EXTRACT: "S1.1",
  S1_COLLECT: "S1.0C",
  S1_CAPTURE: "S1.0C",
  "S0.5": "S0.5",
};

// CR434: Helper to determine if a batch run involves flyer sources
function isFlyerRun(sourcesEnabled: string[]): boolean {
  return sourcesEnabled.includes("FLYER");
}

export function BatchRunStepsPanel({
  batchRunId,
  steps,
  runStatus,
  sourcesEnabled = [],
  currentConfig,
  onStepExecuted,
  onViewOutput,
  isSchedulerRun = false
}: BatchRunStepsPanelProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [executingStep, setExecutingStep] = useState<string | null>(null);
  const [openSteps, setOpenSteps] = useState<Set<string>>(new Set());
  const [executionProgress, setExecutionProgress] = useState<{ current: number; total: number } | null>(null);
  const [retryDialogStep, setRetryDialogStep] = useState<BatchRunStep | null>(null);
  const [s1AutoStartSignal, setS1AutoStartSignal] = useState(0);

  // CR470: Track snippet artifact count for S5_CROP Re-run logic
  const [snippetArtifactCount, setSnippetArtifactCount] = useState<number>(0);

  // CR075-P6: Render All + Continue dialog state
  const [renderAllDialogOpen, setRenderAllDialogOpen] = useState(false);
  const [pendingRenderFlyers, setPendingRenderFlyers] = useState<Array<{
    id: string;
    batch_run_id: string;
    merchant_key: string;
    evidence_json: Record<string, unknown>;
  }>>([]);
  const [renderDialogStepId, setRenderDialogStepId] = useState<string>("");

  const hasAutoStartedRef = useRef(false);

  // CR121: Ref to hold trigger step function (set after handleRunStep is defined)
  const triggerStepRef = useRef<(stepKey: string, options?: { mode?: string }) => Promise<void>>(async () => { });

  // CR308: Fetch latest S2 attempt for attempt-aware status display
  const { latestAttempt: latestS2Attempt, allAttempts: s2Attempts, hasEarlierFailures: s2HasEarlierFailures, refetch: refetchS2Attempts } = useLatestS2Attempt(batchRunId);

  const fetchPendingFlyers = async () => {
    const { data } = await supabase
      .from("cr075_staging_deal_items")
      .select("id, batch_run_id, merchant_key, evidence_json, validated_payload_json")
      .eq("batch_run_id", batchRunId)
      .eq("source_type", "FLYER");

    if (data) {
      // Filter for items that need rendering
      const pending = data.filter((item) => {
        const evidence = item.evidence_json as { render_status?: string; flyer_pages?: unknown[]; flyer_storage_path?: string };
        // CR: Use unwrap helper for deal type
        const dealType = getDealType(item.validated_payload_json);
        const isPlaceholder = dealType === "FLYER_SCAN_PENDING" || dealType === "PDF_NEEDS_RENDERING";
        const hasStoragePath = !!evidence?.flyer_storage_path;
        const isRendered = evidence?.render_status === "RENDERED" && (evidence?.flyer_pages?.length ?? 0) > 0;

        return isPlaceholder && hasStoragePath && !isRendered;
      });

      setPendingRenderFlyers(pending.map(p => ({
        id: p.id,
        batch_run_id: p.batch_run_id,
        merchant_key: p.merchant_key,
        evidence_json: p.evidence_json as Record<string, unknown>,
      })));
    }
  };

  const flyerCaptureMode = resolveFlyerCaptureMode(currentConfig);
  const isImageCaptureMode = flyerCaptureMode === "IMAGE_CAPTURE";
  const isDownloadOnlyMode = flyerCaptureMode === "DOWNLOAD_ONLY";
  const phase1StepGroupSequence = getPhase1RunChainSequence(flyerCaptureMode, sourcesEnabled);
  const stepGroupSequence = STEP_GROUP_SEQUENCE.filter((group) => {
    if (group === "S0.5" || group === "S1" || group === "S2") {
      return phase1StepGroupSequence.includes(group);
    }
    return true;
  });
  const [s1SubstepStatuses, setS1SubstepStatuses] = useState<{
    S1_RENDER: string | null;
    S1_OCR: string | null;
    S1_AI: string | null;
    S1_4: string | null;
  }>({ S1_RENDER: null, S1_OCR: null, S1_AI: null, S1_4: null });
  const [s1StateSnapshot, setS1StateSnapshot] = useState<{
    isComplete: boolean;
    blockedReasons: string[];
    capture?: { terminal?: boolean; scope_frozen?: boolean };
    ocr?: { terminal?: boolean };
    signals?: { terminal?: boolean; ready?: boolean };
    ai?: { terminal?: boolean };
    locate_crop?: { terminal?: boolean };
  } | null>(null);
  const [s1SnapshotStabilityPhase, setS1SnapshotStabilityPhase] = useState<SnapshotStabilityPhase>("SETTLING");
  const s1SnapshotStabilityRef = useRef(createInitialSnapshotStabilityState());
  const [s05RunStatus, setS05RunStatus] = useState<S05StepStatus>("NOT_STARTED");
  const [s05RunStartedAt, setS05RunStartedAt] = useState<string | null>(null);

  const fetchS05RunStatus = useCallback(async () => {
    if (!batchRunId || !sourcesEnabled.includes("FLYER")) {
      setS05RunStatus("SKIPPED");
      setS05RunStartedAt(null);
      return;
    }

    const { data, error } = await supabase
      .from("flyer_download_runs")
      .select("status, started_at")
      .eq("batch_run_id", batchRunId)
      .maybeSingle();

    if (error) {
      console.warn("[BatchRunStepsPanel] Failed to fetch S0.5 status:", error.message);
      return;
    }

    setS05RunStatus(mapS05DownloadRunStatus(data?.status as string | null | undefined));
    setS05RunStartedAt((data?.started_at as string | null) ?? null);
  }, [batchRunId, sourcesEnabled]);

  useEffect(() => {
    void fetchS05RunStatus();
  }, [fetchS05RunStatus, steps]);

  const getS1SubstepStatusForAuto = useCallback((substepKey: "S1_COLLECT" | "S1_RENDER" | "S1_OCR" | "S1_AI" | "S1_4" | "S1_5") => {
    const s1RootStatus = steps.find((s) => s.step_key === "S1")?.status;
    const toStepStatus = (
      status: string | null | undefined,
    ): "NOT_STARTED" | "RUNNING" | "COMPLETE" | "COMPLETE_WITH_TAIL" | "PARTIAL" | "FAILED" | "SKIPPED" => {
      if (!status) return "NOT_STARTED";
      if (status === "RUNNING" || status === "IN_PROGRESS" || status === "QUEUED" || status === "PENDING" || status === "ENQUEUED") return "RUNNING";
      if (status === "COMPLETE") return "COMPLETE";
      if (status === "COMPLETE_WITH_TAIL") return "COMPLETE_WITH_TAIL";
      if (status === "PARTIAL" || status === "COMPLETE_WITH_ERRORS") return "PARTIAL";
      if (status === "SKIPPED") return "SKIPPED";
      if (status === "FAILED" || status === "CANCELLED") return "FAILED";
      return "NOT_STARTED";
    };

    if (substepKey === "S1_5") return "SKIPPED";
    if (substepKey === "S1_COLLECT" || substepKey === "S1_RENDER") {
      if (isImageCaptureMode) return "COMPLETE";
      return toStepStatus(s1RootStatus);
    }
    if (substepKey === "S1_OCR") return toStepStatus(s1SubstepStatuses.S1_OCR);
    if (substepKey === "S1_AI") return toStepStatus(s1SubstepStatuses.S1_AI);
    return toStepStatus(s1SubstepStatuses.S1_4);
  }, [isImageCaptureMode, s1SubstepStatuses, steps]);

  // CR470: Fetch snippet artifact count for S5_CROP Re-run detection
  useEffect(() => {
    if (!batchRunId || !isFlyerRun(sourcesEnabled)) return;
    const fetchSnippetCount = async () => {
      const { count, error } = await supabase
        .from("deal_artifacts")
        .select("id", { count: "exact", head: true })
        .eq("batch_run_id", batchRunId)
        .eq("artifact_type", "flyer_snippet");
      if (!error && count !== null) {
        setSnippetArtifactCount(count);
      }
    };
    fetchSnippetCount();
  }, [batchRunId, sourcesEnabled, steps]);

  // Fetch pending flyers when component mounts or steps change
  useEffect(() => {
    const s1ExtractStep = steps.find(s => s.step_key === "S1_EXTRACT");
    if (s1ExtractStep?.status === "NEEDS_ACTION" || s1ExtractStep?.status === "FAILED") {
      fetchPendingFlyers();
    }
  }, [steps, batchRunId]);

  // Handle "Render All + Continue" button click
  const handleRenderAllAndContinue = (step: BatchRunStep) => {
    setRenderDialogStepId(step.id);
    fetchPendingFlyers().then(() => {
      setRenderAllDialogOpen(true);
    });
  };

  const toggleStep = (stepKey: string) => {
    const newOpen = new Set(openSteps);
    if (newOpen.has(stepKey)) {
      newOpen.delete(stepKey);
    } else {
      newOpen.add(stepKey);
    }
    setOpenSteps(newOpen);
  };

  const handleModeToggle = async (step: BatchRunStep, newMode: string) => {
    try {
      const { error } = await supabase
        .from("cr075_batch_run_steps")
        .update({ mode: newMode })
        .eq("id", step.id);

      if (error) throw error;
      onStepExecuted();
    } catch (err) {
      console.error("Failed to update step mode:", err);
      toast({
        title: "Error",
        description: "Failed to update step mode",
        variant: "destructive",
      });
    }
  };

  // Check for pending renders before running S1_EXTRACT
  const checkPendingRenders = async (): Promise<boolean> => {
    const { data } = await supabase
      .from("cr075_staging_deal_items")
      .select("id, batch_run_id, merchant_key, evidence_json, validated_payload_json")
      .eq("batch_run_id", batchRunId)
      .eq("source_type", "FLYER");

    if (data) {
      const pending = data.filter((item) => {
        const evidence = item.evidence_json as { render_status?: string; flyer_pages?: unknown[]; flyer_storage_path?: string };
        // CR: Use unwrap helper for deal type
        const dealType = getDealType(item.validated_payload_json);
        const isPlaceholder = dealType === "FLYER_SCAN_PENDING" || dealType === "PDF_NEEDS_RENDERING";
        const hasStoragePath = !!evidence?.flyer_storage_path;
        const isRendered = evidence?.render_status === "RENDERED" && (evidence?.flyer_pages?.length ?? 0) > 0;

        return isPlaceholder && hasStoragePath && !isRendered;
      });

      if (pending.length > 0) {
        setPendingRenderFlyers(pending.map(p => ({
          id: p.id,
          batch_run_id: p.batch_run_id,
          merchant_key: p.merchant_key,
          evidence_json: p.evidence_json as Record<string, unknown>,
        })));
        return true; // Has pending renders
      }
    }
    return false; // No pending renders
  };

  // CR186: Added forceReset parameter for stale lock takeover
  // CR708: Added mode parameter for backend-driven reconciliation
  const handleRunStep = async (step: BatchRunStep, options?: { forceReset?: boolean; mode?: string }) => {
    const forceReset = options?.forceReset ?? false;
    const mode = options?.mode ?? "MANUAL";

    // CR075: Auto-render preflight for S1_EXTRACT
    if (step.step_key === "S1_EXTRACT") {
      const hasPending = await checkPendingRenders();
      if (hasPending) {
        // Auto-trigger render dialog instead of running extraction
        setRenderDialogStepId(step.id);
        setRenderAllDialogOpen(true);
        toast({
          title: "Render Required",
          description: "Opening render dialog to process PDFs before extraction...",
        });
        return;
      }
    }

    setExecutingStep(step.step_key);
    setExecutionProgress(null);

    try {
      // CR185: S4 and S5 have their own lease-based lock acquisition in the edge function,
      // so we must NOT set status=RUNNING here for those steps - it would cause a 409 conflict.
      // Other steps still need the UI to set RUNNING before invoking.
      const stepsWithBackendLock = ["S4", "S5"];
      if (!stepsWithBackendLock.includes(step.step_key)) {
        await supabase
          .from("cr075_batch_run_steps")
          .update({ status: "RUNNING", started_at: new Date().toISOString() })
          .eq("id", step.id);
      }

      let response;

      if (step.step_key === "S1") {
        // Run Flyers Collect (queue placeholders)
        response = await supabase.functions.invoke("batch-run-flyer-collect", {
          body: { batch_run_id: batchRunId, step_id: step.id, mode },
        });
      } else if (step.step_key === "S1_EXTRACT") {
        // Run Flyers Extract (OCR + AI extraction)
        response = await supabase.functions.invoke("batch-run-flyer-extract", {
          body: { batch_run_id: batchRunId, step_id: step.id, mode },
        });
      } else if (step.step_key === "S2") {
        // Run Website Collect
        response = await supabase.functions.invoke("batch-run-website-collect", {
          body: { batch_run_id: batchRunId, step_id: step.id, mode },
        });
        // CR308: Refetch S2 attempts after execution to update attempt-aware status
        await refetchS2Attempts();
      } else if (step.step_key === "S3") {
        // Run Forum Signals Kickoff
        response = await supabase.functions.invoke("batch-run-forum-collect", {
          body: { batch_run_id: batchRunId, step_id: step.id },
        });
      } else if (step.step_key === "S4") {
        // CR186: Run Normalize with optional force_reset for stale lock takeover
        response = await supabase.functions.invoke("batch-run-normalize", {
          body: { batch_run_id: batchRunId, step_id: step.id, force_reset: forceReset, mode },
        });
      } else if (step.step_key === "S5") {
        // Run Dedup
        response = await supabase.functions.invoke("batch-run-dedup", {
          body: { batch_run_id: batchRunId, step_id: step.id, mode },
        });
      } else if (step.step_key === "S5_CROP") {
        // CR470: Run S5.1 Locate (AI-driven bbox detection) + auto-crop
        // Pass force_reprocess when re-running with existing artifacts
        const isRerun = snippetArtifactCount > 0;
        response = await supabase.functions.invoke("batch-run-flyer-snippet-locate", {
          body: { batch_run_id: batchRunId, step_id: step.id, limit: 50, auto_crop: true, force_reprocess: isRerun, mode },
        });
      } else if (step.step_key === "S1_4" || step.step_key === "S1_SNIPPET") {
        // CR503: Run S1.4 Snippet Locator (S1_4 is canonical, S1_SNIPPET is legacy alias)
        response = await supabase.functions.invoke("batch-run-flyer-snippet-s14", {
          body: { batch_run_id: batchRunId, mode, auto_crop: true },
        });
      } else {
        // For steps not yet implemented, just mark as skipped
        await supabase
          .from("cr075_batch_run_steps")
          .update({
            status: "SKIPPED",
            finished_at: new Date().toISOString(),
            skipped_reason: "Not implemented in Phase 3",
          })
          .eq("id", step.id);

        toast({
          title: "Step Skipped",
          description: `${step.step_name} is not available in Phase 3`,
        });
        onStepExecuted();
        return;
      }

      if (response?.error) throw response.error;

      const result = response?.data;

      if (result?.skipped) {
        toast({
          title: "Step Skipped",
          description: result.skipped_reason || `${step.step_name} was skipped`,
        });
      } else if (step.step_key === "S5_CROP") {
        // CR471: S5.1 returns locate + crop results; handle NOOP gracefully
        const located = result?.located ?? 0;
        const rejected = result?.rejected ?? 0;
        const errors = result?.errors ?? 0;
        const cropProcessed = result?.crop_result?.processed ?? 0;
        const cropReason = result?.crop_result?.reason;
        const isNoop = located === 0 && rejected === 0 && errors === 0;
        const cropNoop = cropReason === "NO_ELIGIBLE_ROWS" || cropProcessed === 0;

        if (isNoop && result?.skipped_reason === "all_already_located") {
          toast({
            title: "Flyer Snippets",
            description: "All pages already located — nothing to do",
          });
        } else {
          const parts: string[] = [];
          if (located > 0) parts.push(`${located} located`);
          if (rejected > 0) parts.push(`${rejected} rejected`);
          if (errors > 0) parts.push(`${errors} errors`);
          if (cropProcessed > 0) parts.push(`${cropProcessed} cropped`);
          else if (cropNoop && located === 0) parts.push("0 eligible to crop");
          toast({
            title: "Flyer Snippets Complete",
            description: parts.length > 0 ? parts.join(", ") : "0 snippets processed",
          });
        }
      } else if (step.step_key === "S1_4" || step.step_key === "S1_SNIPPET") {
        // CR488: S1.4 Snippet Locator results
        const located = result?.located ?? result?.records_out ?? 0;
        const rejected = result?.rejected ?? 0;
        const errors = result?.errors ?? 0;
        const parts: string[] = [];
        if (located > 0) parts.push(`${located} located`);
        if (rejected > 0) parts.push(`${rejected} rejected`);
        if (errors > 0) parts.push(`${errors} errors`);
        toast({
          title: "S1.5 Locate & Crop Complete",
          description: parts.length > 0 ? parts.join(", ") : "0 snippets processed",
        });
      } else if (step.step_key === "S3") {
        // Special message for Forum Signals
        toast({
          title: "Forum Signals Complete",
          description: `Scanned ${result?.items_scanned || 0} items, found ${result?.candidates_found || 0} candidates`,
        });
      } else if (step.step_key === "S1_EXTRACT") {
        // Special message for Flyer Extract
        toast({
          title: "Flyer Extraction Complete",
          description: `Extracted ${result?.deals_extracted || result?.records_out || 0} deals from flyers`,
        });
      } else {
        toast({
          title: "Step Complete",
          description: `${step.step_name}: ${result?.records_out || 0} deals staged`,
        });
      }

      onStepExecuted();
    } catch (err) {
      console.error("Failed to run step:", err);

      // CRxxx: Capture detailed error info for transparency
      const errorObj = err as any;
      const errorMessage = err instanceof Error ? err.message : "Unknown error";

      // Extract debug info from Supabase function error if available
      const debugInfo: Record<string, unknown> = {
        timestamp: new Date().toISOString(),
        step_id: step.id,
      };

      // Supabase functions.invoke errors may have context attached
      let httpStatus: number | null = null;
      let responseBody: Record<string, unknown> | null = null;

      if (errorObj?.context) {
        httpStatus = errorObj.context?.status;
        debugInfo.http_status = httpStatus;
        debugInfo.http_status_text = errorObj.context?.statusText;
        debugInfo.response_body = errorObj.context?.body;
        responseBody = errorObj.context?.body;
      }

      // If error has additional fields, capture them
      if (errorObj?.code) debugInfo.error_code = errorObj.code;
      if (errorObj?.details) {
        debugInfo.response_body = errorObj.details;
        responseBody = errorObj.details;
      }

      // CR186: Special handling for 409 Conflict - treat as BLOCKED, not FAILED
      const stepsWithConflictHandling = ["S4", "S5"];
      const is409Conflict = httpStatus === 409;
      const shouldBlockNotFail = is409Conflict && stepsWithConflictHandling.includes(step.step_key);

      // Fetch existing config_json to preserve other fields
      const { data: existingStep } = await supabase
        .from("cr075_batch_run_steps")
        .select("config_json, status")
        .eq("id", step.id)
        .single();

      const existingConfig = (existingStep?.config_json as Record<string, unknown>) || {};
      const existingStatus = existingStep?.status;

      if (shouldBlockNotFail) {
        // CR186: For 409, do NOT override status to FAILED
        // The backend already owns the step with its lock, or it's blocked
        const errorKey = responseBody?.error_key as string || "CONCURRENCY_LOCKED";
        const details = responseBody?.details as Record<string, unknown> || {};
        const canTakeover = details?.can_takeover === true;

        // Only update debug info, preserve status from backend
        await supabase
          .from("cr075_batch_run_steps")
          .update({
            config_json: {
              ...existingConfig,
              debug: debugInfo,
              last_conflict: {
                error_key: errorKey,
                message: responseBody?.message || responseBody?.reason,
                details,
                can_takeover: canTakeover,
                timestamp: new Date().toISOString(),
              },
            } as unknown as Record<string, never>,
          })
          .eq("id", step.id);

        // Show a more informative message for conflicts
        if (canTakeover) {
          toast({
            title: "Step Blocked (Stale Lock)",
            description: `${step.step_name} has a stale lock. Use "Force Takeover" to resume.`,
          });
        } else {
          toast({
            title: "Step Blocked",
            description: `${step.step_name} is already running in another invocation.`,
          });
        }
      } else {
        // Normal error handling - set FAILED
        await supabase
          .from("cr075_batch_run_steps")
          .update({
            status: "FAILED",
            finished_at: new Date().toISOString(),
            error_message: errorMessage,
            config_json: {
              ...existingConfig,
              debug: debugInfo,
            } as unknown as Record<string, never>,
          })
          .eq("id", step.id);

        // CR470: Surface meaningful error details
        const errorDetail = httpStatus
          ? `HTTP ${httpStatus}: ${typeof responseBody === 'object' ? JSON.stringify(responseBody).slice(0, 300) : String(responseBody || errorMessage).slice(0, 300)}`
          : errorMessage?.includes("Failed to send")
            ? `Function unreachable — likely CORS/preflight or not deployed in this environment`
            : errorMessage?.slice(0, 300) || "Unknown error";

        toast({
          title: "Error",
          description: `Failed to execute ${step.step_name}: ${errorDetail}`,
          variant: "destructive",
        });
      }

      onStepExecuted();
    } finally {
      setExecutingStep(null);
      setExecutionProgress(null);
    }
  };

  // CR121: Set up trigger step ref after handleRunStep is defined
  useEffect(() => {
    triggerStepRef.current = async (stepKey: string, options?: { mode?: string }) => {
      if (stepKey === "S0.5") {
        const response = await supabase.functions.invoke("batch-run-flyer-download-capture", {
          body: { batch_run_id: batchRunId },
        });

        if (response?.error) {
          throw response.error;
        }

        await fetchS05RunStatus();
        onStepExecuted();
        return;
      }

      const step = steps.find(s => s.step_key === stepKey);
      if (step) {
        await handleRunStep(step, options);
        return;
      }

      throw new Error(`No runnable step found for ${stepKey}`);
    };
  }, [steps, batchRunId, handleRunStep, fetchS05RunStatus, onStepExecuted]);

  const autoModeSteps = steps.map((s) => ({
    step_key: s.step_key,
    status: s.status,
    records_out: s.records_out,
    started_at: s.started_at,
  }));
  const hasS05StepRow = autoModeSteps.some((s) => s.step_key === "S0.5");
  if (!hasS05StepRow && sourcesEnabled.includes("FLYER")) {
    autoModeSteps.unshift({
      step_key: "S0.5",
      status: s05RunStatus,
      records_out: 0,
      started_at: s05RunStartedAt,
    });
  }

  // CR121: Use persisted auto mode hook
  const {
    config: autoModeConfig,
    isSaving: isAutoModeSaving,
    orchestratorState,
    toggleAutoMode,
    setPhase1AutoMode,
    getStepGroupStatus,
    canRunStepGroup,
    getStepBlockedReason, // CR175: Get blocked reason for UI display
    startOrchestration,
    stopOrchestration,
    runStepGroup,
    processOrchestrationTick,
  } = useBatchRunAutoMode({
    batchRunId,
    currentConfigJson: currentConfig,
    sourcesEnabled,
    steps: autoModeSteps,
    onTriggerStep: async (stepKey, options) => {
      if (stepKey === "S1") {
        setS1AutoStartSignal((n) => n + 1);
        return;
      }
      await triggerStepRef.current(stepKey, options);
    },
    onConfigChanged: onStepExecuted,
    getS1SubstepStatus: getS1SubstepStatusForAuto,
    s1StateSnapshot,
    s1SnapshotStabilityPhase,
    stepGroupSequence,
    phase1Groups: phase1StepGroupSequence,
  });

  // CR121: Effect to poll and process orchestration when running
  useEffect(() => {
    if (orchestratorState.isOrchestrating) {
      const interval = setInterval(() => {
        processOrchestrationTick();
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [orchestratorState.isOrchestrating, processOrchestrationTick]);

  useEffect(() => {
    if (!orchestratorState.isOrchestrating || orchestratorState.currentGroup !== "S0.5") {
      return;
    }

    const interval = setInterval(() => {
      void fetchS05RunStatus();
    }, 3000);

    return () => clearInterval(interval);
  }, [orchestratorState.isOrchestrating, orchestratorState.currentGroup, fetchS05RunStatus]);

  const isPhase1AutoEnabled = phase1StepGroupSequence.every((group) => autoModeConfig.auto_mode[group]);
  const isBatchAutoRunEnabled = typeof autoModeConfig.batch_auto_run_enabled === "boolean"
    ? autoModeConfig.batch_auto_run_enabled
    : isPhase1AutoEnabled;

  const getNextRunnablePhase1Group = useCallback((): StepGroupKey | null => {
    for (const group of phase1StepGroupSequence) {
      if (!autoModeConfig.auto_mode[group]) continue;
      const status = getStepGroupStatus(group);
      if (status === "COMPLETE") continue;
      return group;
    }
    return null;
  }, [autoModeConfig.auto_mode, getStepGroupStatus, phase1StepGroupSequence]);

  const handleTogglePhase1Auto = useCallback(async (enabled: boolean) => {
    await setPhase1AutoMode(enabled);
  }, [setPhase1AutoMode]);

  const handleRunAuto = useCallback(async () => {
    const startGroup = getNextRunnablePhase1Group();
    if (!startGroup) {
      toast({
        title: "Nothing to Run",
        description: "S1 → S2 → S4 → S5 is already complete or disabled.",
      });
      return;
    }
    await startOrchestration(startGroup);
  }, [getNextRunnablePhase1Group, startOrchestration, toast]);

  const handleStopAuto = useCallback(async () => {
    await stopOrchestration("USER_STOP");
  }, [stopOrchestration]);

  /**
   * CRxxx: Removed auto-start on load logic (user wants manual start only).
   * Original logic was:
   * useEffect(() => {
   *   if (hasAutoStartedRef.current) return;
   *   if (orchestratorState.isOrchestrating) return;
   *   if (!isPhase1AutoEnabled) return;
   *   if (autoModeConfig.auto_state?.running_group) return;
   *   const nextGroup = getNextRunnablePhase1Group();
   *   if (!nextGroup) return;
   *   hasAutoStartedRef.current = true;
   *   void startOrchestration(nextGroup);
   * }, [...]);
   */

  // CR121: Handle S1 complete callback from FlyerPipelineStepsPanel
  const handleS1Complete = useCallback(() => {
    onStepExecuted();
  }, [onStepExecuted]);

  // CR150: Track S4 trigger to prevent duplicate invocations across re-renders/session
  const s4TriggeredRef = useRef<string | null>(null);
  // CR150: Track S5 trigger similarly
  const s5TriggeredRef = useRef<string | null>(null);
  // CR415/CR502: S5_CROP trigger (compat-only)
  const s5CropTriggeredRef = useRef<string | null>(null);

  // CR150 FIX: Determine enabled sources from sourcesEnabled prop (reliable), not from step status
  // sourcesEnabled is ["FLYER", "WEB", "FORUM"] etc. from batch run config
  const flyerSourceEnabled = sourcesEnabled.includes("FLYER");
  const webSourceEnabled = sourcesEnabled.includes("WEB");
  const forumSourceEnabled = sourcesEnabled.includes("FORUM");

  // CR121/CR150: Monitor step completions for cross-group chaining
  // Only trigger S4 when ALL enabled collection steps are COMPLETE/PARTIAL/SKIPPED
  // CR175: Fetch S1 substep statuses whenever steps change
  // Quick fix for infinite loop: use a stringified version of steps to avoid reference churn
  const stepsStringified = JSON.stringify(steps.map(s => `${s.id}-${s.status}`));

  useEffect(() => {
    if (!flyerSourceEnabled || !batchRunId) return;

    const fetchS1Substeps = async () => {
      // Fetch latest attempts for each substep
      const { data: attempts } = await supabase
        .from("cr075_batch_run_step_attempts")
        .select("step_key, status, diagnostics_json, created_at")
        .eq("batch_run_id", batchRunId)
        .in("step_key", ["S1_RENDER", "S1_OCR", "S1_SIGNALS", "S1_AI", "S1_4", "S1_SNIPPET"])
        .order("created_at", { ascending: false });

      const latest: Record<string, { status: string; diagnostics_json: Record<string, unknown> | null }> = {};
      for (const attempt of attempts || []) {
        if (!latest[attempt.step_key]) {
          latest[attempt.step_key] = {
            status: getEffectiveAttemptStatus(attempt as any),
            diagnostics_json: (attempt.diagnostics_json as Record<string, unknown> | null) ?? null,
          };
        }
      }

      setS1SubstepStatuses((prev) => {
        const next = {
          S1_RENDER: latest["S1_RENDER"]?.status || null,
          S1_OCR: latest["S1_OCR"]?.status || null,
          S1_AI: latest["S1_AI"]?.status || null,
          S1_4: latest["S1_4"]?.status || latest["S1_SNIPPET"]?.status || null,
        };
        if (
          prev.S1_RENDER === next.S1_RENDER &&
          prev.S1_OCR === next.S1_OCR &&
          prev.S1_AI === next.S1_AI &&
          prev.S1_4 === next.S1_4
        ) {
          return prev;
        }
        return next;
      });

      const dedupeReasons = (reasons: string[]): string[] => {
        const seen = new Set<string>();
        const out: string[] = [];
        for (const reason of reasons) {
          const value = String(reason ?? "").trim();
          if (!value || seen.has(value)) continue;
          seen.add(value);
          out.push(value);
        }
        return out;
      };

      const s14Status = latest["S1_4"]?.status ?? latest["S1_SNIPPET"]?.status ?? null;
      const { snapshot: canonicalSnapshot, source: canonicalSnapshotSource } = selectCanonicalS1Snapshot({
        s14Status,
        s14Diagnostics: (latest["S1_4"]?.diagnostics_json as Record<string, unknown> | null | undefined)
          ?? (latest["S1_SNIPPET"]?.diagnostics_json as Record<string, unknown> | null | undefined)
          ?? null,
        aiDiagnostics: (latest["S1_AI"]?.diagnostics_json as Record<string, unknown> | null | undefined) ?? null,
        signalsDiagnostics: (latest["S1_SIGNALS"]?.diagnostics_json as Record<string, unknown> | null | undefined) ?? null,
        ocrDiagnostics: (latest["S1_OCR"]?.diagnostics_json as Record<string, unknown> | null | undefined) ?? null,
      });

      const applySnapshotStability = (nextSnapshot: {
        isComplete: boolean;
        blockedReasons: string[];
        capture?: { terminal?: boolean; scope_frozen?: boolean };
        ocr?: { terminal?: boolean };
        signals?: { terminal?: boolean; ready?: boolean };
        ai?: { terminal?: boolean };
        locate_crop?: { terminal?: boolean };
      }) => {
        const nextStability = advanceSnapshotStability(
          s1SnapshotStabilityRef.current,
          nextSnapshot,
          Date.now(),
        );
        if (nextStability.phase !== s1SnapshotStabilityRef.current.phase) {
          console.log(`[S1 Snapshot Stability] ${nextStability.phase === "SETTLING" ? "SETTLING_ENTER" : "SETTLING_EXIT"} polls=${nextStability.stablePollCount} stableMs=${nextStability.stableMs}`);
        }
        s1SnapshotStabilityRef.current = nextStability;
        setS1SnapshotStabilityPhase(nextStability.phase);
      };

      if (canonicalSnapshot && typeof canonicalSnapshot === "object") {
        const nextSnapshot = {
          isComplete: Boolean(canonicalSnapshot.isComplete),
          blockedReasons: dedupeReasons(Array.isArray(canonicalSnapshot.blockedReasons) ? canonicalSnapshot.blockedReasons : []),
          capture: canonicalSnapshot.capture,
          ocr: canonicalSnapshot.ocr,
          signals: canonicalSnapshot.signals,
          ai: canonicalSnapshot.ai,
          locate_crop: canonicalSnapshot.locate_crop,
        };
        setS1StateSnapshot(nextSnapshot);
        applySnapshotStability(nextSnapshot);
        console.log(`[S4 AutoTrigger] Canonical S1 snapshot source=${canonicalSnapshotSource} complete=${Boolean(canonicalSnapshot.isComplete)}, blocked=${JSON.stringify(canonicalSnapshot.blockedReasons || [])}`);
      } else {
        const terminalStatuses = new Set(["COMPLETE", "COMPLETE_WITH_ERRORS", "PARTIAL", "FAILED", "CANCELLED", "SKIPPED"]);
        const ocrDiag = latest["S1_OCR"]?.diagnostics_json ?? null;
        const signalsDiag = latest["S1_SIGNALS"]?.diagnostics_json ?? null;
        const aiDiag = latest["S1_AI"]?.diagnostics_json ?? null;
        const locateStatus = s14Status;
        const locateAttemptedOnce = (attempts || []).some((attempt) =>
          (attempt.step_key === "S1_4" || attempt.step_key === "S1_SNIPPET") &&
          terminalStatuses.has(String(attempt.status ?? "").toUpperCase())
        );
        const ocrScope = (ocrDiag?.output_scope as Record<string, unknown> | undefined) ?? undefined;
        const ocrAttemptIds = Array.isArray(ocrScope?.attempt_ids)
          ? (ocrScope?.attempt_ids as string[])
          : [];
        const signalsInputScope = (signalsDiag?.input_ocr_scope as Record<string, unknown> | undefined) ?? undefined;
        const signalsScopeIds = Array.isArray(signalsInputScope?.attempt_ids)
          ? (signalsInputScope?.attempt_ids as string[])
          : Array.isArray(signalsDiag?.input_ocr_run_ids)
            ? (signalsDiag?.input_ocr_run_ids as string[])
            : [];
        const scopeMatch = ocrAttemptIds.length === 0 || ocrAttemptIds.every((id) => signalsScopeIds.includes(id));
        const scopeFrozen = Boolean(ocrScope?.scope_frozen);
        const seeded = Number((ocrScope?.page_counts as any)?.seeded ?? 0) || 0;
        const terminalCount =
          (Number((ocrScope?.page_counts as any)?.succeeded ?? 0) || 0) +
          (Number((ocrScope?.page_counts as any)?.failed ?? 0) || 0) +
          (Number((ocrScope?.page_counts as any)?.skipped ?? 0) || 0);
        const queuePending =
          (Number((ocrScope?.page_counts as any)?.queued ?? 0) || 0) +
          (Number((ocrScope?.page_counts as any)?.running ?? 0) || 0);
        const reconciliationOk = seeded > 0 ? seeded === terminalCount : true;
        const signalsTerminal = terminalStatuses.has(String(latest["S1_SIGNALS"]?.status ?? "").toUpperCase());
        const aiTerminal = terminalStatuses.has(String(latest["S1_AI"]?.status ?? "").toUpperCase());
        const locateTerminal = terminalStatuses.has(String(locateStatus ?? "").toUpperCase());
        const noWorkTerminal =
          signalsTerminal &&
          (
            Number((signalsDiag?.candidates_found as number | undefined) ?? -1) === 0 ||
            Number((aiDiag?.candidates_total as number | undefined) ?? -1) === 0
          );
        const blockedReasons: string[] = [];
        if (!scopeFrozen) blockedReasons.push("OCR_SCOPE_UNSTABLE");
        if (!reconciliationOk || queuePending > 0) blockedReasons.push("OCR_RECONCILIATION_PENDING");
        if (signalsTerminal && !scopeMatch) blockedReasons.push("SIGNALS_SCOPE_MISMATCH");
        if (aiTerminal && !locateAttemptedOnce) blockedReasons.push("S1_5_NOT_ATTEMPTED");
        const isComplete = aiTerminal && locateAttemptedOnce;
        if (noWorkTerminal) blockedReasons.push("NO_WORK_TERMINAL");
        const fallbackSnapshot = {
          isComplete,
          blockedReasons: dedupeReasons(blockedReasons),
          locate_crop: {
            terminal: locateTerminal,
            attempted_once: locateAttemptedOnce,
            latest_status: locateStatus,
          },
          completion_rule: {
            strategy: "AI_TERMINAL_AND_S1_5_ATTEMPTED_ONCE",
            ai_terminal: aiTerminal,
            s1_5_attempted_once: locateAttemptedOnce,
            granted_by_attempted_once_semantics: isComplete,
            no_work_terminal_observed: noWorkTerminal,
            used_for_s4_unblock: true,
          },
        };
        setS1StateSnapshot(fallbackSnapshot);
        applySnapshotStability(fallbackSnapshot);
        console.log(`[S4 AutoTrigger] Fallback S1 snapshot source=${canonicalSnapshotSource} complete=${isComplete}, blocked=${JSON.stringify(blockedReasons)}`);
      }

      console.log(`[S4 AutoTrigger] CR175 substeps: RENDER=${latest["S1_RENDER"]?.status || "NOT_FOUND"}, OCR=${latest["S1_OCR"]?.status || "NOT_FOUND"}, AI=${latest["S1_AI"]?.status || "NOT_FOUND"}`);
    };

    fetchS1Substeps();
  }, [batchRunId, flyerSourceEnabled, stepsStringified]); // Refetch when steps change

  useEffect(() => {
    // Phase-1 uses top-level orchestrator only. Legacy local auto-trigger path is disabled.
    if (!(currentConfig as Record<string, unknown> | undefined)?.legacy_local_auto_trigger_enabled) {
      return;
    }

    const s1Step = steps.find(s => s.step_key === "S1");
    const s2Step = steps.find(s => s.step_key === "S2");
    const s3Step = steps.find(s => s.step_key === "S3");
    const s4Step = steps.find(s => s.step_key === "S4");
    const s5Step = steps.find(s => s.step_key === "S5");

    // CR150 FIX: Use sourcesEnabled prop to determine what's enabled, not step status
    // A step is "done" if: (1) that source is not enabled, OR (2) the step is in a terminal state
    const terminalStates = ["COMPLETE", "PARTIAL", "SKIPPED", "FAILED"];

    const s1Done = !flyerSourceEnabled || (s1Step && terminalStates.includes(s1Step.status));
    const s2Done = !webSourceEnabled || (s2Step && terminalStates.includes(s2Step.status));
    const s3Done = !forumSourceEnabled || (s3Step && terminalStates.includes(s3Step.status));

    // All collection is done when all enabled sources have finished
    const anySourceEnabled = flyerSourceEnabled || webSourceEnabled || forumSourceEnabled;
    const allCollectionDone = s1Done && s2Done && s3Done && anySourceEnabled;

    // CR150: Only count as ready if at least one source actually succeeded (not all failed)
    const anySucceeded =
      (flyerSourceEnabled && s1Step && ["COMPLETE", "PARTIAL"].includes(s1Step.status)) ||
      (webSourceEnabled && s2Step && ["COMPLETE", "PARTIAL"].includes(s2Step.status)) ||
      (forumSourceEnabled && s3Step && ["COMPLETE", "PARTIAL"].includes(s3Step.status));

    // CR175: Additional S1 substep gating for FLYER source
    // S4 is NOT ready if ANY required S1 substep is still pending/running
    let s1SubstepsComplete = true;
    let s1SubstepBlocker: string | null = null;

    if (flyerSourceEnabled && s1Step && terminalStates.includes(s1Step.status)) {
      const terminalSubstepStatuses = ["COMPLETE", "PARTIAL", "SKIPPED", "FAILED", "CANCELLED"];
      const blockingStatuses = ["RUNNING", "IN_PROGRESS", "QUEUED", "ENQUEUED", "PENDING"];

      // Check S1_RENDER
      if (s1SubstepStatuses.S1_RENDER) {
        if (blockingStatuses.includes(s1SubstepStatuses.S1_RENDER)) {
          s1SubstepsComplete = false;
          s1SubstepBlocker = `S1.1 Render is ${s1SubstepStatuses.S1_RENDER}`;
        } else if (!terminalSubstepStatuses.includes(s1SubstepStatuses.S1_RENDER)) {
          s1SubstepsComplete = false;
          s1SubstepBlocker = `S1.1 Render status=${s1SubstepStatuses.S1_RENDER}`;
        }
      } else {
        // No render attempt = not yet run
        s1SubstepsComplete = false;
        s1SubstepBlocker = "Waiting for S1.1 Render";
      }

      // Check S1_OCR (only if RENDER passed)
      if (s1SubstepsComplete && s1SubstepStatuses.S1_OCR) {
        if (blockingStatuses.includes(s1SubstepStatuses.S1_OCR)) {
          s1SubstepsComplete = false;
          s1SubstepBlocker = `S1.2 OCR is ${s1SubstepStatuses.S1_OCR}`;
        } else if (!terminalSubstepStatuses.includes(s1SubstepStatuses.S1_OCR)) {
          s1SubstepsComplete = false;
          s1SubstepBlocker = `S1.2 OCR status=${s1SubstepStatuses.S1_OCR}`;
        }
      } else if (s1SubstepsComplete && !s1SubstepStatuses.S1_OCR) {
        // No OCR attempt = not yet run
        s1SubstepsComplete = false;
        s1SubstepBlocker = "Waiting for S1.2 OCR";
      }

      // Check S1_AI (only if OCR passed and AI is enabled)
      // Note: AI might not be enabled - we check if OCR found candidates that would trigger AI
      // For safety, if AI exists in the config or has been started, require terminal
      if (s1SubstepsComplete && s1SubstepStatuses.S1_AI !== null) {
        if (blockingStatuses.includes(s1SubstepStatuses.S1_AI!)) {
          s1SubstepsComplete = false;
          s1SubstepBlocker = `S1.4 Deep Scan is ${s1SubstepStatuses.S1_AI}`;
        } else if (!terminalSubstepStatuses.includes(s1SubstepStatuses.S1_AI!)) {
          s1SubstepsComplete = false;
          s1SubstepBlocker = `S1.4 Deep Scan status=${s1SubstepStatuses.S1_AI}`;
        }
      }
      // If S1_AI is null, AI was never started (likely no candidates), so we allow proceeding
    }

    const s1ReadyForDownstream = isS1ReadyForDownstream(
      typeof s1StateSnapshot?.isComplete === "boolean" ? s1StateSnapshot.isComplete : null,
      s1SubstepsComplete,
    );
    const settlingGuardActive = shouldBlockS4ForSnapshotSettling(
      s1SnapshotStabilityPhase,
      s1StateSnapshot?.isComplete ?? null,
    );
    const readyForS4 = allCollectionDone && anySucceeded && s1ReadyForDownstream && !settlingGuardActive;

    // Debug log for troubleshooting
    console.log(`[S4 AutoTrigger] sources=[FLYER:${flyerSourceEnabled}, WEB:${webSourceEnabled}, FORUM:${forumSourceEnabled}]`);
    console.log(`[S4 AutoTrigger] S1=${s1Step?.status || 'N/A'}(done=${s1Done}), S2=${s2Step?.status || 'N/A'}(done=${s2Done}), S3=${s3Step?.status || 'N/A'}(done=${s3Done})`);
    console.log(`[S4 AutoTrigger] allCollectionDone=${allCollectionDone}, anySucceeded=${anySucceeded}, s1SubstepsComplete=${s1SubstepsComplete}`);
    if (s1SubstepBlocker) {
      console.log(`[S4 AutoTrigger] CR175 BLOCKED: ${s1SubstepBlocker}`);
    }
    console.log(`[S4 AutoTrigger] readyForS4=${readyForS4} (settling=${settlingGuardActive})`);

    // CR150: Trigger S4 only when ALL collection is complete, at least one succeeded, and S4 hasn't been triggered yet
    const s4Status = s4Step?.status;
    const s4NotRunYet = s4Status === "NOT_STARTED" || s4Status === "PENDING";
    const s4NotTriggeredThisSession = s4TriggeredRef.current !== batchRunId;

    if (readyForS4 && autoModeConfig.auto_mode.S4 && canRunStepGroup("S4", { strict: true }) && s4NotRunYet && s4NotTriggeredThisSession) {
      console.log(`[S4 AutoTrigger] ✓ Triggering S4 - all enabled collection sources complete AND S1 substeps terminal`);
      s4TriggeredRef.current = batchRunId; // Mark as triggered to prevent duplicate
      triggerStepRef.current("S4");
    }

    // CR150: S5 trigger - only after S4 is COMPLETE and S5 hasn't been triggered
    const s5Status = s5Step?.status;
    const s5NotRunYet = s5Status === "NOT_STARTED" || s5Status === "PENDING";
    const s5NotTriggeredThisSession = s5TriggeredRef.current !== batchRunId;

    if (s4Step?.status === "COMPLETE" && autoModeConfig.auto_mode.S5 && canRunStepGroup("S5") && s5NotRunYet && s5NotTriggeredThisSession) {
      console.log(`[S5 AutoTrigger] ✓ Triggering S5 - S4 complete`);
      s5TriggeredRef.current = batchRunId;
      triggerStepRef.current("S5");
    }

    // CR502: S5_CROP / S1_5 auto-trigger DISABLED — compat-only, deprecated
    // Legacy auto-trigger code removed. Auto-mode skips S1_5 because it's compat-only.
  }, [steps, autoModeConfig.auto_mode, canRunStepGroup, batchRunId, flyerSourceEnabled, webSourceEnabled, forumSourceEnabled, s1SubstepStatuses, currentConfig, s1StateSnapshot, s1SnapshotStabilityPhase]);

  const canRunStep = (step: BatchRunStep, index: number): boolean => {
    if (runStatus === "CANCELLED" || runStatus === "FAILED") return false;
    if (step.status === "RUNNING") return false;

    // S1, S2, and S3 can always be re-run
    if (step.step_key === "S1" || step.step_key === "S2" || step.step_key === "S3") {
      return true;
    }

    // S1_EXTRACT (deprecated) requires S1 complete with pending flyers
    if (step.step_key === "S1_EXTRACT") {
      const s1 = steps.find((s) => s.step_key === "S1");
      return s1?.status === "COMPLETE" || s1?.status === "PARTIAL";
    }

    // CR175: S4 requires all S1 substeps (including S1.3 AI) to be terminal when AI is enabled
    // Use the hook's canRunStepGroup which properly checks S1 substep status
    if (step.step_key === "S4") {
      return canRunStepGroup("S4");
    }

    // S5 requires S4 complete
    if (step.step_key === "S5") {
      const s4 = steps.find((s) => s.step_key === "S4");
      return s4?.status === "COMPLETE";
    }

    // CR502: S5_CROP and S1_5 are compat-only — not runnable from UI
    if (step.step_key === "S5_CROP" || step.step_key === "S1_5") {
      return false; // Deprecated (compat-only). Snippet cropping is being folded into S1.4.
    }

    // CR503: S1_4 can be manually run when S1_AI is complete (backfill-on-demand if no step row exists)
    if (step.step_key === "S1_4" || step.step_key === "S1_SNIPPET") {
      const s1AiComplete = s1SubstepStatuses.S1_AI === "COMPLETE";
      return s1AiComplete;
    }

    // S6 requires S5 complete with records (S5_CROP is NOT required - soft optional)
    if (step.step_key === "S6") {
      const s5 = steps.find((s) => s.step_key === "S5");
      return s5?.status === "COMPLETE" && (s5?.records_out ?? 0) > 0;
    }

    // S7 requires S6 complete
    if (step.step_key === "S7") {
      const s6 = steps.find((s) => s.step_key === "S6");
      return s6?.status === "COMPLETE";
    }

    // S8 requires S7 complete
    if (step.step_key === "S8") {
      const s7 = steps.find((s) => s.step_key === "S7");
      return s7?.status === "COMPLETE";
    }

    return true;
  };

  // Get explicit disabled reason for S6-S8 steps
  const getStepDisabledReason = (stepKey: string): { reason: string; canNavigate: boolean } => {
    const s5 = steps.find((s) => s.step_key === "S5");
    const s6 = steps.find((s) => s.step_key === "S6");
    const s7 = steps.find((s) => s.step_key === "S7");

    if (stepKey === "S6") {
      if (!s5 || s5.status !== "COMPLETE") {
        return { reason: "Waiting for S5 (Dedup) to complete", canNavigate: false };
      }
      if ((s5.records_out ?? 0) === 0) {
        return { reason: `No deduped deals available (${s5.records_out ?? 0}). Review inputs in Deduped View.`, canNavigate: true };
      }
      return { reason: "Ready to review", canNavigate: true };
    }

    if (stepKey === "S7") {
      if (!s6 || s6.status !== "COMPLETE") {
        return { reason: "Waiting for S6 (Review) to complete", canNavigate: false };
      }
      return { reason: "Ready to publish", canNavigate: true };
    }

    if (stepKey === "S8") {
      if (!s7 || s7.status !== "COMPLETE") {
        return { reason: "Waiting for S7 (Publish) to complete", canNavigate: false };
      }
      return { reason: "Ready for cleanup", canNavigate: false };
    }

    return { reason: "Review & Publish handled in Deduped View", canNavigate: true };
  };

  const formatDuration = (ms: number | null): string => {
    if (!ms) return "-";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // CR082/CR383/CR121: flyerCaptureMode already declared above (line ~225)
  const isDownloadOnly = flyerCaptureMode === "DOWNLOAD_ONLY";

  // Get collect steps only (S1, S1_EXTRACT, S2, S3)
  // CR082: When FLYER source is enabled, hide S1 from Collection Steps (moved to Flyer Pipeline as 1.0)
  // CR080: When FLYER source is enabled, hide S1_EXTRACT (deprecated - replaced by 1.1/1.2/1.3)
  const collectSteps = steps.filter((s) => {
    if (isDownloadOnly && s.step_key !== "S1") {
      return false; // In DOWNLOAD mode, hide Website Collection and Forum Collection steps
    }
    if (sourcesEnabled.includes("FLYER") && s.step_key === "S1") {
      return false; // S1 is now shown in FlyerPipelineStepsPanel as 1.0 (or Download progress)
    }
    if (sourcesEnabled.includes("FLYER") && s.step_key === "S1_EXTRACT") {
      return false; // S1_EXTRACT is deprecated - replaced by 1.1 Render, 1.2 OCR, 1.3 AI
    }
    return ["S1", "S1_EXTRACT", "S2", "S3"].includes(s.step_key);
  });
  // Processing steps (S4, S5)
  // CR488: S5_CROP/S1_5 moved to S1 Flyer Extraction section (rendered inside FlyerPipelineStepsPanel)
  // CR503: S1_4/S1_SNIPPET also rendered inside FlyerPipelineStepsPanel
  const processingSteps = steps.filter((s) => {
    if (isDownloadOnly) return false; // Hide completely in download mode
    return ["S4", "S5"].includes(s.step_key);
  });
  // Future steps (S6+) - disabled for now
  // CR080: Also exclude S1_EXTRACT from future steps (it's deprecated)
  // CR488: Exclude S5_CROP, S1_5, S1_4, S1_SNIPPET from future steps (now in S1 Flyer section)
  const futureSteps = steps.filter((s) => {
    if (isDownloadOnly) return false; // Hide completely in download mode
    return !["S1", "S1_EXTRACT", "S2", "S3", "S4", "S5", "S5_CROP", "S1_5", "S1_4", "S1_SNIPPET"].includes(s.step_key);
  });

  // CR082: Get S1 step info for FlyerPipelineStepsPanel
  const s1Step = steps.find((s) => s.step_key === "S1");
  const s1CollectStepInfo = s1Step ? {
    status: s1Step.status as "NOT_STARTED" | "RUNNING" | "COMPLETE" | "PARTIAL" | "FAILED",
    flyersQueued: s1Step.records_out || 0,
  } : undefined;

  // CR082: Handler for running S1 from FlyerPipelineStepsPanel
  const handleRunS1Collect = async () => {
    if (!s1Step) {
      throw new Error("S1 step not found");
    }
    // Manual S1.0 clicks should execute collect immediately.
    // runStepGroup("S1") routes through auto-orchestration signaling and may no-op
    // when S1 auto-mode is disabled, which leaves S1.1 blocked.
    await handleRunStep(s1Step);
  };

  const runTopLevelStep = useCallback(async (step: BatchRunStep) => {
    if (step.step_key === "S0.5" || step.step_key === "S1" || step.step_key === "S2" || step.step_key === "S4" || step.step_key === "S5") {
      await runStepGroup(step.step_key as StepGroupKey);
      return;
    }
    await handleRunStep(step);
  }, [runStepGroup, handleRunStep]);

  const autoCurrentStep = orchestratorState.currentGroup ?? autoModeConfig.auto_state?.running_group ?? null;
  const autoNextStep = getNextRunnablePhase1Group();
  const autoStopReason = orchestratorState.errorMessage ?? autoModeConfig.auto_state?.last_error ?? null;
  const showPerStepAutoToggles = Boolean((currentConfig as Record<string, unknown> | undefined)?.legacy_per_step_auto_controls);

  return (
    <Card>
      <CardHeader className="space-y-3">
        <div className="flex items-start justify-between gap-3">
          <CardTitle>Pipeline Steps</CardTitle>
          <div className="flex items-center gap-2">
            <Label htmlFor="pipeline-auto" className="text-xs text-muted-foreground">Batch Auto-Run (chains steps)</Label>
            <Switch
              id="pipeline-auto"
              checked={isBatchAutoRunEnabled}
              onCheckedChange={handleTogglePhase1Auto}
              disabled={isAutoModeSaving || executingStep !== null}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={handleStopAuto}
              disabled={!orchestratorState.isOrchestrating}
              className="gap-1.5"
            >
              <StopCircle className="h-4 w-4" />
              Stop
            </Button>
            <Button
              size="sm"
              onClick={handleRunAuto}
              disabled={orchestratorState.isOrchestrating}
              className="gap-1.5"
            >
              <Play className="h-4 w-4" />
              Run Chain
            </Button>
          </div>
        </div>
        <CardDescription>
          <span>
            Current: {autoCurrentStep ?? "IDLE"} · Next: {autoNextStep ?? "NONE"}
          </span>
          {autoStopReason && <span> · Stop reason: {autoStopReason}</span>}
          <span> · Runs enabled top-level steps in order ({phase1StepGroupSequence.join("→")}).</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* CR553: SCAN SETTINGS heading */}
        {sourcesEnabled.includes("FLYER") && (
          <>
            <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Scan Settings
            </Label>
            <S0ScanSettingsAccordion
              batchRunId={batchRunId}
              currentConfig={currentConfig}
              onConfigSaved={onStepExecuted}
            />
          </>
        )}

        {/* CR123: Collection Steps section - includes S1 Flyer + S2/S3 Website/Forum */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Collection Steps
          </Label>

          {/* CR383/CR384/CR551: S0.5 Download Flyer tile — hidden in IMAGE_CAPTURE mode */}
          {sourcesEnabled.includes("FLYER") && !isImageCaptureMode && (
            <S05CollectionStepTile
              batchRunId={batchRunId}
              flyerCaptureMode={flyerCaptureMode as any}
              autoModeEnabled={autoModeConfig.auto_mode["S0.5"]}
              onToggleAutoMode={(enabled) => toggleAutoMode("S0.5", enabled)}
              isOrchestrating={orchestratorState.isOrchestrating && orchestratorState.currentGroup === "S0.5"}
            />
          )}

          {/* CR080/CR082/CR121/CR123: S1 Flyer Pipeline Steps (1.0 Collect → 1.1 Render → 1.2 OCR → 1.3 AI) - now inside Collection Steps */}
          {sourcesEnabled.includes("FLYER") && !isDownloadOnlyMode && (
            <FlyerPipelineStepsPanel
              batchRunId={batchRunId}
              onRefresh={onStepExecuted}
              onRunCollect={handleRunS1Collect}
              collectStepInfo={s1CollectStepInfo}
              autoModeEnabled={autoModeConfig.auto_mode.S1}
              isParentOrchestrating={orchestratorState.isOrchestrating && orchestratorState.currentGroup === "S1"}
              onS1Complete={handleS1Complete}
              flyerCaptureMode={flyerCaptureMode}
              hideLocalAutoControls
              parentAutoStartSignal={s1AutoStartSignal}
            />
          )}

          {/* S2/S3: Website Collect, Forum Collect */}
          {collectSteps.map((step, index) => {
            // CR308: For S2, derive effective status from latest attempt
            const isS2 = step.step_key === "S2";
            const s2StatusInfo = isS2 && latestS2Attempt
              ? deriveEffectiveS2Status(step.status, latestS2Attempt)
              : null;
            const effectiveStatus = s2StatusInfo?.effectiveStatus || step.status;
            const isOpen = openSteps.has(step.step_key);
            const isExecuting = executingStep === step.step_key;
            const canRun = canRunStep(step, index);
            const stepIcon = stepIcons[step.step_key] || null;
            const blockedReasonsForDisplay = !canRun && effectiveStatus !== "RUNNING" && effectiveStatus !== "COMPLETE"
              ? ["PREREQUISITES_NOT_MET"]
              : [];
            const displayStatus = getPipelineDisplayStatus({
              status: isExecuting ? "RUNNING" : effectiveStatus,
              isRunning: isExecuting,
              blockedReasons: blockedReasonsForDisplay,
            });
            const configStatusKey = mapDisplayStatusToConfigKey(displayStatus, effectiveStatus);
            const statusInfo = stepStatusConfig[configStatusKey] || stepStatusConfig.NOT_STARTED;
            const blockedHelper = getPipelineStatusHelperText(displayStatus, { batchAutoRunEnabled: isBatchAutoRunEnabled });

            // CR308: For S2, show latest attempt records if available
            const displayRecordsOut = isS2 && latestS2Attempt?.records_out != null
              ? latestS2Attempt.records_out
              : step.records_out;

            return (
              <Collapsible key={step.id} open={isOpen} onOpenChange={() => toggleStep(step.step_key)}>
                <div className="border rounded-lg">
                  <CollapsibleTrigger asChild>
                    <button className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`${statusInfo.color}`}>
                          {isExecuting ? <Loader2 className="h-4 w-4 animate-spin" /> : statusInfo.icon}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {stepIcon}
                            {step.step_key}: {step.step_name}
                            <Badge variant={step.mode === "AUTO" ? "default" : "outline"} className="text-xs">
                              {step.mode}
                            </Badge>
                            {/* CR308: Show override badge when latest attempt succeeded after earlier failures */}
                            {isS2 && s2StatusInfo?.attemptOverride && (
                              <Badge variant="outline" className="text-xs border-primary/50 text-primary">
                                Retry OK
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {isExecuting ? "Running..." : statusInfo.label}
                            {step.duration_ms && ` · ${formatDuration(step.duration_ms)}`}
                            {/* CR075: S1 outputs are flyers queued, S1_EXTRACT outputs are deals extracted */}
                            {step.step_key === "S1" && step.records_out > 0 && (
                              <span> · {step.records_out} flyers queued <Badge variant="outline" className="ml-1 text-xs">Pending OCR</Badge></span>
                            )}
                            {step.step_key === "S1_EXTRACT" && step.records_out > 0 && (
                              <span> · {step.records_out} deals extracted <Badge variant="default" className="ml-1 text-xs bg-green-600">Extracted</Badge></span>
                            )}
                            {!["S1", "S1_EXTRACT"].includes(step.step_key) && displayRecordsOut > 0 && ` · ${displayRecordsOut} records`}
                            {/* CR308: Show attempt count for S2 */}
                            {isS2 && s2Attempts.length > 1 && (
                              <span className="ml-1 text-xs">({s2Attempts.length} attempts)</span>
                            )}
                          </div>
                          {!canRun && effectiveStatus !== "RUNNING" && effectiveStatus !== "COMPLETE" && blockedHelper && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 cursor-help">
                                    <Clock className="h-3 w-3" />
                                    Waiting for prerequisites
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="text-xs">{blockedHelper}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {/* CR075-P6: "Render All + Continue" for S1_EXTRACT with NEEDS_ACTION status */}
                        {step.step_key === "S1_EXTRACT" && step.status === "NEEDS_ACTION" && (
                          <Button
                            size="sm"
                            variant="default"
                            className="bg-amber-600 hover:bg-amber-700"
                            disabled={isExecuting}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRenderAllAndContinue(step);
                            }}
                          >
                            <ImageIcon className="h-4 w-4 mr-1" />
                            Render All + Continue
                          </Button>
                        )}
                        {/* Retry button for PARTIAL/FAILED steps with units */}
                        {(step.status === "PARTIAL" || step.status === "FAILED") && (step.units_failed ?? 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={isExecuting}
                            onClick={(e) => {
                              e.stopPropagation();
                              setRetryDialogStep(step);
                            }}
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Retry
                          </Button>
                        )}
                        {/* CR121: Auto Mode toggle for S2 */}
                        {showPerStepAutoToggles && step.step_key === "S2" && (
                          <div className="flex items-center gap-2">
                              <Label
                                htmlFor={`auto-${step.step_key}`}
                                className="text-xs text-muted-foreground cursor-pointer"
                              >
                                Step Auto
                              </Label>
                            <Switch
                              id={`auto-${step.step_key}`}
                              checked={autoModeConfig.auto_mode.S2}
                              onCheckedChange={(checked) => toggleAutoMode("S2", checked)}
                              disabled={isAutoModeSaving || isExecuting}
                              className="data-[state=checked]:bg-primary"
                            />
                          </div>
                        )}
                        {canRun && step.step_key !== "S1_EXTRACT" && (
                          <Button
                            size="sm"
                            variant={step.status === "COMPLETE" || step.status === "PARTIAL" ? "outline" : "default"}
                            disabled={isExecuting}
                            onClick={(e) => {
                              e.stopPropagation();
                              runTopLevelStep(step);
                            }}
                          >
                            {isExecuting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : step.status === "COMPLETE" || step.status === "PARTIAL" ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Re-run
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-1" />
                                Run
                              </>
                            )}
                          </Button>
                        )}
                        {/* S1_EXTRACT: Always show Run button - it will auto-preflight render if needed */}
                        {canRun && step.step_key === "S1_EXTRACT" && step.status !== "NEEDS_ACTION" && (
                          <Button
                            size="sm"
                            variant={step.status === "COMPLETE" || step.status === "PARTIAL" ? "outline" : "default"}
                            disabled={isExecuting}
                            onClick={(e) => {
                              e.stopPropagation();
                              runTopLevelStep(step);
                            }}
                          >
                            {isExecuting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : step.status === "COMPLETE" || step.status === "PARTIAL" ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Re-run
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-1" />
                                Run
                              </>
                            )}
                          </Button>
                        )}
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-2 border-t space-y-3">
                      {isExecuting && executionProgress && (
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span>Progress</span>
                            <span>{executionProgress.current} / {executionProgress.total}</span>
                          </div>
                          <Progress value={(executionProgress.current / executionProgress.total) * 100} />
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label htmlFor={`mode-${step.id}`} className="text-sm">Auto Mode</Label>
                          {isSchedulerRun && (
                            <span className="text-xs text-muted-foreground">(Scheduler)</span>
                          )}
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span>
                                <Switch
                                  id={`mode-${step.id}`}
                                  checked={step.mode === "AUTO"}
                                  onCheckedChange={(checked) => handleModeToggle(step, checked ? "AUTO" : "MANUAL")}
                                  disabled={isSchedulerRun}
                                />
                              </span>
                            </TooltipTrigger>
                            {isSchedulerRun && (
                              <TooltipContent>
                                <p>Mode controlled by Scheduler "Run until" setting</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        </TooltipProvider>
                      </div>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Records In:</span>{" "}
                          <span className="font-medium">{step.records_in}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            {step.step_key === "S1" ? "Flyers Queued:" :
                              step.step_key === "S1_EXTRACT" ? "Deals Extracted:" : "Records Out:"}
                          </span>{" "}
                          <span className="font-medium">{step.records_out}</span>
                          {step.step_key === "S1" && step.records_out > 0 && (
                            <Badge variant="outline" className="ml-2 text-xs bg-yellow-100 text-yellow-800">
                              Pending OCR
                            </Badge>
                          )}
                          {step.step_key === "S1_EXTRACT" && step.records_out > 0 && (
                            <Badge variant="default" className="ml-2 text-xs bg-green-600">
                              Extracted
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Units summary for steps with unit tracking */}
                      {(step.units_total ?? 0) > 0 && (
                        <div className="flex items-center gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Units:</span>{" "}
                            <span className="font-medium text-green-600">{step.units_succeeded ?? 0}</span>
                            <span className="text-muted-foreground">/</span>
                            <span className="font-medium">{step.units_total}</span>
                            {(() => {
                              const failedUnits = (step.config_json as any)?.reconciliation?.failed_units;
                              const summary = deriveFailureSummary(failedUnits);
                              if (summary.label) {
                                // Use yellow for timeout-only, red for hard failures
                                const colorClass = summary.isTimeout ? "text-yellow-600" : "text-destructive";
                                return (
                                  <span className={`${colorClass} ml-1`}>
                                    ({summary.label})
                                  </span>
                                );
                              }
                              // Fallback to legacy display if no reconciliation data
                              if ((step.units_failed ?? 0) > 0) {
                                return (
                                  <span className="text-destructive ml-1">
                                    ({step.units_failed} failed)
                                  </span>
                                );
                              }
                              return null;
                            })()}
                          </div>
                          {(step.attempt_count ?? 0) > 1 && (
                            <Badge variant="secondary" className="text-xs">
                              Attempt #{step.attempt_count}
                            </Badge>
                          )}
                        </div>
                      )}

                      {/* S2 Website Collect: Progress stats from config_json */}
                      {step.step_key === "S2" && step.config_json && (
                        <div className="space-y-3">
                          {/* Contract info banner */}
                          {(step.config_json as any).use_poc_deals_bridge && (
                            <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-700 dark:text-yellow-300">
                              ⚠️ Using temporary poc_deals bridge (adapter: {(step.config_json as any).adapter_version || "unknown"})
                            </div>
                          )}

                          {/* Contract version + scope + attempt ID */}
                          <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {(step.config_json as any).contract_version || "CR075_v1"}
                            </Badge>
                            {(step.config_json as any).scope_locked && (
                              <span>Scope: {(step.config_json as any).scope_locked} merchants</span>
                            )}
                            {(step.config_json as any).source_attempt_id && (
                              <Badge variant="secondary" className="text-xs font-mono">
                                Attempt: {(step.config_json as any).source_attempt_id.slice(0, 8)}
                              </Badge>
                            )}
                          </div>

                          {/* Attempt-scoped progress details */}
                          {(step.config_json as any).progress && (
                            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                Attempt-Scoped Metrics
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Merchants:</span>{" "}
                                  <span className="font-medium text-green-600">
                                    {(step.config_json as any).progress.merchants_succeeded || 0}
                                  </span>
                                  <span className="text-muted-foreground">/</span>
                                  <span className="font-medium">{(step.config_json as any).progress.merchants_attempted || 0}</span>
                                  {(() => {
                                    const failedUnits = (step.config_json as any)?.reconciliation?.failed_units;
                                    const summary = deriveFailureSummary(failedUnits);
                                    if (summary.label) {
                                      const colorClass = summary.isTimeout ? "text-yellow-600" : "text-destructive";
                                      return <span className={`${colorClass} ml-1`}>({summary.label})</span>;
                                    }
                                    // Fallback to legacy display
                                    if ((step.config_json as any).progress.merchants_failed > 0) {
                                      return (
                                        <span className="text-destructive ml-1">
                                          ({(step.config_json as any).progress.merchants_failed} failed)
                                        </span>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Candidates:</span>{" "}
                                  <span className="font-medium">{(step.config_json as any).progress.candidates_found || 0}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Validated (DB):</span>{" "}
                                  <span className="font-medium text-primary">
                                    {(step.config_json as any).reconciliation?.attempt_metrics?.validated_count_for_attempt ??
                                      (step.config_json as any).progress.deals_validated ?? 0}
                                  </span>
                                  {/* Show response-reported if different */}
                                  {(step.config_json as any).reconciliation?.attempt_metrics?.response_reported &&
                                    (step.config_json as any).reconciliation.attempt_metrics.response_reported.deals_validated !==
                                    (step.config_json as any).reconciliation.attempt_metrics.validated_count_for_attempt && (
                                      <span className="text-xs text-muted-foreground ml-1">
                                        (response: {(step.config_json as any).reconciliation.attempt_metrics.response_reported.deals_validated})
                                      </span>
                                    )}
                                </div>
                              </div>
                              <div className="grid grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Staged (DB):</span>{" "}
                                  <span className="font-medium text-primary">
                                    {(step.config_json as any).reconciliation?.attempt_metrics?.staged_db_count_for_attempt ??
                                      (step.config_json as any).progress.deals_staged ?? 0}
                                  </span>
                                </div>
                                {(step.config_json as any).progress.domain_rejections > 0 && (
                                  <div>
                                    <span className="text-muted-foreground">Domain Rejected:</span>{" "}
                                    <span className="font-medium text-yellow-600">{(step.config_json as any).progress.domain_rejections}</span>
                                  </div>
                                )}
                                {(step.config_json as any).progress.contract_errors > 0 && (
                                  <div>
                                    <Badge variant="destructive" className="text-xs">
                                      {(step.config_json as any).progress.contract_errors} contract errors
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Reconciliation block summary */}
                          {(step.config_json as any).reconciliation && (
                            <div className={`p-3 rounded-lg border text-sm ${(step.config_json as any).reconciliation.reconciliation_passed
                              ? "bg-green-500/10 border-green-500/30"
                              : "bg-destructive/10 border-destructive/30"
                              }`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium">
                                  Reconciliation: {(step.config_json as any).reconciliation.reconciliation_passed ? "✓ PASSED" : "✗ FAILED"}
                                </span>
                                <Badge variant="outline" className="text-xs">
                                  {(step.config_json as any).reconciliation.staging_mode}
                                </Badge>
                              </div>
                              {(step.config_json as any).reconciliation.attempt_metrics && (
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div>
                                    <span className="text-muted-foreground">Expected Staged:</span>{" "}
                                    <span className="font-mono">{(step.config_json as any).reconciliation.attempt_metrics.expected_staged_count}</span>
                                  </div>
                                  <div>
                                    <span className="text-muted-foreground">Actual Staged:</span>{" "}
                                    <span className="font-mono">{(step.config_json as any).reconciliation.attempt_metrics.staged_db_count_for_attempt}</span>
                                  </div>
                                </div>
                              )}
                              {/* Recompute status reason client-side for correct timeout semantics */}
                              {(() => {
                                const recon = (step.config_json as any).reconciliation;
                                const failedUnits = recon?.failed_units;
                                const stagedCount = recon?.attempt_metrics?.staged_db_count_for_attempt ?? step.records_out;

                                if (!failedUnits?.length) {
                                  // No failed units, show original reason if present
                                  return recon.step_status_reason ? (
                                    <div className="mt-2 text-xs text-muted-foreground">
                                      {recon.step_status_reason}
                                    </div>
                                  ) : null;
                                }

                                // Recompute with correct timeout vs failed semantics
                                const summary = deriveFailureSummary(failedUnits);
                                const merchantNames = failedUnits.map((u: any) => u.merchant_key).join(", ");
                                const actionLabel = summary.isTimeout
                                  ? `${summary.timedOutCount} merchant(s) timed out`
                                  : summary.hardFailCount > 0 && summary.timedOutCount > 0
                                    ? `${summary.timedOutCount} timed out, ${summary.hardFailCount} failed`
                                    : `${summary.hardFailCount} merchant(s) failed`;
                                const statusReason = `Partial output: ${stagedCount} deals staged; ${actionLabel} (${merchantNames})`;
                                const textColor = summary.isTimeout ? "text-yellow-600" : "text-muted-foreground";

                                return (
                                  <div className={`mt-2 text-xs ${textColor}`}>
                                    {statusReason}
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* Failed units summary */}
                          {(step.config_json as any).reconciliation?.failed_units?.length > 0 && (
                            (() => {
                              const failedUnits = (step.config_json as any).reconciliation.failed_units;
                              const summary = deriveFailureSummary(failedUnits);
                              // Use yellow background for timeout-only, red for hard failures
                              const bgClass = summary.isTimeout
                                ? "bg-yellow-500/10 text-yellow-700 dark:text-yellow-400"
                                : "bg-destructive/10 text-destructive";
                              const headerLabel = summary.isTimeout
                                ? `${failedUnits.length} merchant(s) timed out:`
                                : summary.hardFailCount > 0 && summary.timedOutCount > 0
                                  ? `${failedUnits.length} merchant(s) had issues:`
                                  : `${failedUnits.length} merchant(s) failed:`;
                              return (
                                <div className={`p-2 ${bgClass} rounded text-sm`}>
                                  <div className="font-medium mb-1">{headerLabel}</div>
                                  <ul className="text-xs list-disc list-inside max-h-24 overflow-auto">
                                    {failedUnits.slice(0, 5).map((u: any, i: number) => {
                                      const reason = u.failure_reason || u.status;
                                      const reasonLabel = reason === "TIMED_OUT" ? "Timed out" : reason;
                                      return (
                                        <li key={i}>
                                          <span className="font-medium">{u.merchant_key}</span>: {reasonLabel}
                                          {u.staged_before_timeout > 0 && (
                                            <span className="text-green-600 ml-1">
                                              ({u.staged_before_timeout} staged)
                                            </span>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              );
                            })()
                          )}

                          {/* Contract test result */}
                          {(step.config_json as any).contract_test && (
                            <div className={`p-2 rounded text-sm ${(step.config_json as any).contract_test.passed
                              ? "bg-green-500/10 text-green-700 dark:text-green-300 border border-green-500/30"
                              : "bg-destructive/10 text-destructive border border-destructive/30"
                              }`}>
                              <div className="font-medium">
                                Contract Test: {(step.config_json as any).contract_test.passed ? "✓ PASSED" : "✗ FAILED"}
                              </div>
                              <div className="text-xs mt-1">
                                {(step.config_json as any).contract_test.rows_passed}/{(step.config_json as any).contract_test.rows_tested} rows validated
                              </div>
                              {(step.config_json as any).contract_test.errors?.length > 0 && (
                                <ul className="text-xs mt-1 list-disc list-inside">
                                  {(step.config_json as any).contract_test.errors.slice(0, 3).map((e: any, i: number) => (
                                    <li key={i}>{e.stagingItemKey}: {e.errors.join("; ")}</li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          )}

                          {/* Stage errors */}
                          {(step.config_json as any).stage_errors?.length > 0 && (
                            <div className="p-2 bg-destructive/10 text-destructive rounded text-sm">
                              <div className="font-medium">{(step.config_json as any).stage_errors.length} staging errors</div>
                              <ul className="text-xs mt-1 list-disc list-inside max-h-24 overflow-auto">
                                {(step.config_json as any).stage_errors.slice(0, 5).map((e: any, i: number) => (
                                  <li key={i}>{e.merchantKey}: {e.errors.join("; ")}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      {/* CR308: S2 Attempts History */}
                      {isS2 && s2Attempts.length > 0 && (
                        <div className="space-y-2 p-3 bg-muted/30 rounded-lg border">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center justify-between">
                            <span>Attempts History ({s2Attempts.length})</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                refetchS2Attempts();
                              }}
                            >
                              <RefreshCw className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="space-y-1 max-h-32 overflow-y-auto">
                            {s2Attempts.map((attempt, i) => {
                              const isLatest = i === 0;
                              const statusColor = attempt.status === "COMPLETE"
                                ? "text-green-600"
                                : attempt.status === "FAILED" || attempt.status === "TIMED_OUT"
                                  ? "text-destructive"
                                  : "text-muted-foreground";
                              return (
                                <div
                                  key={attempt.id}
                                  className={`flex items-center justify-between text-xs p-1.5 rounded ${isLatest ? "bg-primary/10 border border-primary/20" : "bg-muted/50"}`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className={`font-medium ${statusColor}`}>
                                      {attempt.status}
                                    </span>
                                    {isLatest && (
                                      <Badge variant="outline" className="text-xs h-4 px-1">Latest</Badge>
                                    )}
                                    {attempt.error_message && (
                                      <span className="text-destructive truncate max-w-[150px]" title={attempt.error_message}>
                                        {attempt.error_message}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 text-muted-foreground">
                                    <span>In: {attempt.records_in ?? 0}</span>
                                    <span>Out: {attempt.records_out ?? 0}</span>
                                    <span className="font-mono text-xs">
                                      {new Date(attempt.created_at).toLocaleTimeString()}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {s2HasEarlierFailures && latestS2Attempt?.status === "COMPLETE" && (
                            <div className="text-xs text-primary p-1.5 bg-primary/5 rounded border border-primary/20">
                              ✓ Latest attempt succeeded after earlier failures
                            </div>
                          )}
                        </div>
                      )}

                      {/* CRxxx: Enhanced error display with debug details */}
                      {/* CR308: For S2, hide step error if latest attempt succeeded */}
                      {!(isS2 && s2StatusInfo?.attemptOverride) && (
                        <StepErrorDetails
                          errorMessage={step.error_message}
                          configJson={step.config_json}
                          stepId={step.id}
                        />
                      )}

                      {step.skipped_reason && (
                        <div className="p-2 bg-muted text-muted-foreground rounded text-sm">
                          Skipped: {step.skipped_reason}
                        </div>
                      )}

                      {/* S1_EXTRACT: Flyer Diagnostics Panel (v2-style) */}
                      {step.step_key === "S1_EXTRACT" && step.config_json && (step.config_json as any).flyer_extract_diagnostics && (
                        <S1ExtractDiagnosticsPanel
                          diagnostics={(step.config_json as any).flyer_extract_diagnostics}
                        />
                      )}

                      {/* CR319: S2 Web Scan Debug Panel */}
                      {isS2 && (step.status === "COMPLETE" || step.status === "PARTIAL" || step.status === "FAILED") && (
                        <S2WebScanDebugPanel
                          batchRunId={step.batch_run_id}
                          parentPocRunId={(step.config_json as any)?.parent_poc_run_id || null}
                        />
                      )}

                      {/* S3 Forum Signals: Special output view with counts + link */}
                      {step.step_key === "S3" && step.status === "COMPLETE" && step.config_json && (
                        <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Items Scanned:</span>{" "}
                              <span className="font-medium">{(step.config_json as any).items_scanned ?? step.records_in}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Candidates Found:</span>{" "}
                              <span className="font-medium text-primary">{(step.config_json as any).candidates_found ?? 0}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Promoted:</span>{" "}
                              <span className="font-medium">{(step.config_json as any).candidates_promoted ?? 0}</span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-2"
                            onClick={() => navigate("/admin/forum-deals")}
                          >
                            <ExternalLink className="h-4 w-4" />
                            Open Forum Deals Admin
                          </Button>
                        </div>
                      )}

                      {/* S1/S2: Standard View Output button */}
                      {step.step_key !== "S3" && (step.status === "COMPLETE" || step.records_out > 0) && onViewOutput && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => onViewOutput(step.step_key)}
                        >
                          <Eye className="h-4 w-4" />
                          View Output
                        </Button>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>

        {/* Processing Steps (S4-S5) - Now Active */}
        <div className="space-y-1.5">
          <Label className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
            Processing Steps
          </Label>
          {processingSteps.map((step, index) => {
            const isOpen = openSteps.has(step.step_key);
            const isExecuting = executingStep === step.step_key;
            const canRun = canRunStep(step, index);
            const stepIcon = stepIcons[step.step_key] || null;
            const blockedReasonCode = !canRun && step.status !== "RUNNING" && step.status !== "COMPLETE"
              ? getStepBlockedReason(step.step_key as StepGroupKey, { strict: true })
              : null;
            const blockedReason = !canRun && step.status !== "RUNNING" && step.status !== "COMPLETE"
              ? getStepBlockedReason(step.step_key as StepGroupKey)
              : null;
            const displayStatus = getPipelineDisplayStatus({
              status: isExecuting ? "RUNNING" : step.status,
              isRunning: isExecuting,
              blockedReasons: blockedReasonCode ? [blockedReasonCode] : [],
            });
            const configStatusKey = mapDisplayStatusToConfigKey(displayStatus, step.status);
            const statusInfo = stepStatusConfig[configStatusKey] || stepStatusConfig.NOT_STARTED;
            const blockedHelper = getPipelineStatusHelperText(displayStatus, { batchAutoRunEnabled: isBatchAutoRunEnabled });

            return (
              <Collapsible key={step.id} open={isOpen} onOpenChange={() => toggleStep(step.step_key)}>
                <div className="border rounded-lg">
                  <CollapsibleTrigger asChild>
                    <button className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className={`${step.step_key === "S5_CROP" && snippetArtifactCount > 0 && step.status !== "COMPLETE" ? "text-green-500" : statusInfo.color}`}>
                          {isExecuting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                            step.step_key === "S5_CROP" && snippetArtifactCount > 0 && step.status !== "COMPLETE"
                              ? <CheckCircle2 className="h-4 w-4" />
                              : statusInfo.icon
                          )}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            {stepIcon}
                            {stepDisplayKey[step.step_key] || step.step_key}: {(step.step_key === "S5_CROP" || step.step_key === "S1_5") ? (
                              // CR502: Show as deprecated legacy snippet crop
                              "Legacy Snippet Crop (Deprecated)"
                            ) : (step.step_key === "S1_SNIPPET" || step.step_key === "S1_4")
                              ? "Locate & Crop"
                              : step.step_key === "S1_AI"
                                ? "Deep Scan (Candidates Only)"
                                : step.step_name}
                            {/* CR502: Show deprecated badge for S5_CROP / S1_5 */}
                            {(step.step_key === "S5_CROP" || step.step_key === "S1_5") && (
                              <Badge variant="outline" className="text-xs text-muted-foreground" title="Deprecated (compat-only). Locate & Crop now runs in S1.5.">Deprecated</Badge>
                            )}
                            {/* CR121: Show Auto badge if auto mode is enabled */}
                            {autoModeConfig.auto_mode[step.step_key as StepGroupKey] && (
                              <Badge variant="secondary" className="text-xs gap-1">
                                <FastForward className="h-3 w-3" />
                                Auto
                              </Badge>
                            )}
                            {!autoModeConfig.auto_mode[step.step_key as StepGroupKey] && (
                              <Badge variant={step.mode === "AUTO" ? "default" : "outline"} className="text-xs">
                                {step.mode}
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {isExecuting ? "Running..." : (
                              step.step_key === "S5_CROP" && snippetArtifactCount > 0 && step.status !== "COMPLETE"
                                ? "Complete"
                                : statusInfo.label
                            )}
                            {step.duration_ms && ` · ${formatDuration(step.duration_ms)}`}
                            {step.records_out > 0 && ` · ${step.records_out} records`}
                            {step.step_key === "S5_CROP" && snippetArtifactCount > 0 && ` · ${snippetArtifactCount} artifacts`}
                          </div>
                          {/* CR175: Show blocked reason when step can't run */}
                          {!canRun && step.status !== "RUNNING" && step.status !== "COMPLETE" && blockedReason && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 cursor-help">
                                    <Clock className="h-3 w-3" />
                                    {blockedReason}
                                  </div>
                                </TooltipTrigger>
                                {blockedHelper && (
                                  <TooltipContent>
                                    <p className="text-xs">{blockedHelper}</p>
                                  </TooltipContent>
                                )}
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        {/* CR485: S5_CROP disabled tooltip when S1_SNIPPET is active */}
                        {step.step_key === "S5_CROP" && (steps.some(s => s.step_key === "S1_4" || s.step_key === "S1_SNIPPET") || s1SubstepStatuses.S1_4) && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-xs text-muted-foreground cursor-help">Locate & Crop now runs as S1.5</Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Locate & Crop now runs as S1.5; this tile is diagnostics only.</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {/* CR121/CR434: Auto Mode toggle for S4/S5/S5_CROP — hide for S5_CROP when S1_SNIPPET active */}
                        {showPerStepAutoToggles && (step.step_key === "S4" || step.step_key === "S5" || (step.step_key === "S5_CROP" && !(steps.some(s => s.step_key === "S1_4" || s.step_key === "S1_SNIPPET") || s1SubstepStatuses.S1_4))) && (
                          <div className="flex items-center gap-2">
                            <Label
                              htmlFor={`auto-${step.step_key}`}
                              className="text-xs text-muted-foreground cursor-pointer"
                            >
                              Step Auto
                            </Label>
                            <Switch
                              id={`auto-${step.step_key}`}
                              checked={autoModeConfig.auto_mode[step.step_key as StepGroupKey]}
                              onCheckedChange={(checked) => toggleAutoMode(step.step_key as StepGroupKey, checked)}
                              disabled={isAutoModeSaving || isExecuting}
                              className="data-[state=checked]:bg-primary"
                            />
                          </div>
                        )}
                        {/* CR485/CR488: Hide Run/Re-run for S5_CROP when S1_SNIPPET active; S1_SNIPPET now has its own Re-run */}
                        {canRun && !(step.step_key === "S5_CROP" && (steps.some(s => s.step_key === "S1_4" || s.step_key === "S1_SNIPPET") || s1SubstepStatuses.S1_4)) && (
                          <Button
                            size="sm"
                            variant={(step.status === "COMPLETE" || (step.step_key === "S5_CROP" && snippetArtifactCount > 0)) ? "outline" : "default"}
                            disabled={isExecuting}
                            onClick={(e) => {
                              e.stopPropagation();
                              runTopLevelStep(step);
                            }}
                          >
                            {isExecuting ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (step.status === "COMPLETE" || (step.step_key === "S5_CROP" && snippetArtifactCount > 0)) ? (
                              <>
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Re-run
                              </>
                            ) : (
                              <>
                                <Play className="h-4 w-4 mr-1" />
                                Run
                              </>
                            )}
                          </Button>
                        )}
                        {/* CR177: Restart Normalize button for stuck S4 - now calls edge function with force_reset */}
                        {step.step_key === "S4" && step.status === "RUNNING" && (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={isExecuting}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                // CR177: Call edge function with force_reset=true to bypass concurrency guard
                                const { data, error } = await supabase.functions.invoke("batch-run-normalize", {
                                  body: {
                                    batch_run_id: batchRunId,
                                    step_id: step.id,
                                    force_reset: true,
                                  },
                                });

                                if (error) {
                                  toast({ title: "Force Reset Error", description: error.message, variant: "destructive" });
                                } else if (data?.blocked) {
                                  // Still blocked (shouldn't happen with force_reset, but handle it)
                                  toast({
                                    title: "Still Blocked",
                                    description: data.reason || "Could not reset",
                                    variant: "destructive"
                                  });
                                } else {
                                  toast({
                                    title: "S4 Force Reset",
                                    description: `Normalize executed. Records out: ${data?.records_out ?? 0}`
                                  });
                                  onStepExecuted?.();
                                }
                              } catch (err) {
                                toast({
                                  title: "Error",
                                  description: err instanceof Error ? err.message : "Unknown error",
                                  variant: "destructive"
                                });
                              }
                            }}
                            title="Force reset and re-run Normalize step (bypasses concurrency guard)"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Force Reset
                          </Button>
                        )}
                        {/* CR180: Force Reset S5 Dedup for stuck runs */}
                        {step.step_key === "S5" && step.status === "RUNNING" && (
                          <Button
                            size="sm"
                            variant="destructive"
                            disabled={isExecuting}
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const { data, error } = await supabase.functions.invoke("batch-run-dedup", {
                                  body: {
                                    batch_run_id: batchRunId,
                                    step_id: step.id,
                                    force_reset: true,
                                  },
                                });

                                if (error) {
                                  toast({ title: "Force Reset Error", description: error.message, variant: "destructive" });
                                } else if (data?.blocked) {
                                  toast({
                                    title: "Still Blocked",
                                    description: data.reason || "Could not reset",
                                    variant: "destructive"
                                  });
                                } else {
                                  toast({
                                    title: "S5 Force Reset",
                                    description: `Dedup executed. Records out: ${data?.records_out ?? 0}`
                                  });
                                  onStepExecuted?.();
                                }
                              } catch (err) {
                                toast({
                                  title: "Error",
                                  description: err instanceof Error ? err.message : "Unknown error",
                                  variant: "destructive"
                                });
                              }
                            }}
                            title="Force reset and re-run Dedup step (bypasses concurrency guard)"
                          >
                            <RotateCcw className="h-4 w-4 mr-1" />
                            Force Reset
                          </Button>
                        )}
                        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="px-4 pb-4 pt-2 border-t space-y-3">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Records In:</span>{" "}
                          <span className="font-medium">{step.records_in}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Records Out:</span>{" "}
                          <span className="font-medium">{step.records_out}</span>
                        </div>
                      </div>

                      {/* CR470: S5.1 Workflow Pack Selector */}
                      {step.step_key === "S5_CROP" && (
                        <S5WorkflowPackSelector
                          batchRunId={batchRunId}
                          currentConfig={currentConfig}
                        />
                      )}

                      {/* CR470: S5.1 Locate Diagnostics */}
                      {step.step_key === "S5_CROP" && step.config_json && (step.config_json as any).summary && (
                        <div className="space-y-1 p-2 bg-muted/50 rounded-lg text-xs font-mono">
                          <div className="text-muted-foreground font-sans font-medium text-xs uppercase tracking-wide mb-1">S5.1 Locate Results</div>
                          <div>located: {(step.config_json as any).summary.located}, rejected: {(step.config_json as any).summary.rejected}, errors: {(step.config_json as any).summary.errors}</div>
                          {(step.config_json as any).prompt_key && <div>prompt: {(step.config_json as any).prompt_key}</div>}
                          {(step.config_json as any).model_used && <div>model: {(step.config_json as any).model_used}</div>}
                        </div>
                      )}

                      {/* CR101: Rule Pack info for S4/S5 */}
                      {(step.step_key === "S4" || step.step_key === "S5") && step.config_json && (
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-muted-foreground">Rule Pack:</span>
                          {(step.config_json as any).rule_pack_id ? (
                            <>
                              <span className="font-medium">
                                {(step.config_json as any).rule_pack_name || "Unknown"} v{(step.config_json as any).rule_pack_version || "?"}
                              </span>
                              <Badge
                                variant={(step.config_json as any).rule_pack_source === "embedded_default" ? "destructive" : "secondary"}
                                className="text-[10px]"
                              >
                                {(step.config_json as any).rule_pack_source === "run_override" ? "Run Override" :
                                  (step.config_json as any).rule_pack_source === "resolved" ? "Resolved" :
                                    (step.config_json as any).rule_pack_source === "missing" ? "Missing" :
                                      "Embedded Default"}
                              </Badge>
                            </>
                          ) : (step.config_json as any).rule_pack_source === "embedded_default" ? (
                            <>
                              <span className="font-medium italic text-muted-foreground">Embedded Defaults</span>
                              <Badge variant="destructive" className="text-[10px] gap-1">
                                <AlertCircle className="h-3 w-3" />
                                Embedded Default
                              </Badge>
                            </>
                          ) : step.status === "RUNNING" ? (
                            <span className="text-muted-foreground italic">resolving…</span>
                          ) : (
                            <span className="text-muted-foreground italic">Not yet executed</span>
                          )}
                        </div>
                      )}

                      {/* CR474: Warning banner when S4 has zero records in */}
                      {step.step_key === "S4" && step.status === "COMPLETE" && (step.records_in ?? 0) === 0 && (
                        <div className="space-y-2">
                          <div className="flex items-start gap-2 p-2.5 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-xs">
                            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                            <div className="space-y-1">
                              <p className="font-medium">No staging deals found for this batch</p>
                              <p>Ensure S1.4 (Deep Scan) staged extracted deals successfully. In Image Capture mode, staging creation must happen in S1.4 — otherwise S4 will always be empty.</p>
                              {(() => {
                                const s1AiStep = steps.find(s => s.step_key === "S1_AI");
                                const dealsExtracted = s1AiStep ? (s1AiStep.records_out ?? 0) : 0;
                                const s1AiStatus = s1AiStep?.status;
                                if (s1AiStep && dealsExtracted > 0) {
                                  return (
                                    <p className="font-medium text-amber-900 dark:text-amber-200">
                                      ⚠ S1.4 extracted {dealsExtracted} deal(s) (status: {s1AiStatus}) but no staged deals were created. Check S1.4 reconciliation.
                                    </p>
                                  );
                                }
                                return null;
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* CR177: S4 Input Stats with full diagnostics */}
                      {step.step_key === "S4" && step.config_json && (
                        <div className="space-y-1">
                          <div className="text-xs text-muted-foreground font-mono">
                            S4 input: staged_found={((step.config_json as any).staged_found ?? (step.config_json as any).normalize_diagnostics?.staged_found) ?? "?"},
                            {" "}eligible_found={((step.config_json as any).eligible_found ?? (step.config_json as any).normalize_diagnostics?.eligible_found) ?? "?"},
                            {" "}skipped={((step.config_json as any).skipped ?? (step.config_json as any).normalize_diagnostics?.skipped) ?? "?"}
                          </div>
                          {/* CR177: Show source type breakdown if available */}
                          {(() => {
                            const diag = (step.config_json as any).normalize_diagnostics ||
                              (step.config_json as any).blocked_diagnostics ||
                              (step.config_json as any).diagnostic?.input_diagnostics;
                            const bySourceType = diag?.by_source_type;
                            const byPayloadStatus = diag?.by_payload_status;
                            const skippedReasons = diag?.skipped_reasons || diag?.skip_reason_counts;
                            const phase = diag?.phase;

                            if (!bySourceType && !byPayloadStatus) return null;

                            return (
                              <div className="text-xs text-muted-foreground font-mono bg-muted/50 p-1 rounded">
                                {bySourceType && Object.keys(bySourceType).length > 0 && (
                                  <div>by_source: {Object.entries(bySourceType).map(([k, v]) => `${k}=${v}`).join(", ")}</div>
                                )}
                                {byPayloadStatus && (
                                  <div>payload: has={byPayloadStatus.has_payload ?? "?"}, empty={byPayloadStatus.empty_payload ?? "?"}</div>
                                )}
                                {skippedReasons && Object.keys(skippedReasons).length > 0 && (
                                  <div className="text-yellow-600">skip_reasons: {Object.entries(skippedReasons).map(([k, v]) => `${k}=${v}`).join(", ")}</div>
                                )}
                                {phase && <div>phase: {phase}</div>}
                              </div>
                            );
                          })()}
                          {/* CR177: Show why if zero eligible */}
                          {(() => {
                            const diag = (step.config_json as any).diagnostic;
                            if (!diag?.why) return null;
                            return (
                              <div className="text-xs text-amber-600 font-mono bg-amber-50 p-1 rounded border border-amber-200">
                                {diag.why}
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* CR183: S4 Advanced Diagnostics Accordion */}
                      {step.step_key === "S4" && step.config_json && (
                        <S4AdvancedDiagnostics
                          configJson={step.config_json as Record<string, unknown>}
                          stepStatus={step.status}
                          batchRunId={batchRunId}
                          stepId={step.id}
                          onForceTakeover={() => handleRunStep(step, { forceReset: true })}
                        />
                      )}

                      {/* CR102: Dedup Mode info for S5 */}
                      {step.step_key === "S5" && (
                        <div className="flex items-center gap-2 text-sm flex-wrap">
                          <span className="text-muted-foreground">Dedup Mode:</span>
                          {(() => {
                            const cfg = step.config_json as any;
                            const mode = cfg?.dedupe_mode || cfg?.dedup_diagnostics?.dedupe_mode;
                            const aiEnabled = cfg?.dedupe_ai_enabled || cfg?.dedup_diagnostics?.ai_enabled;
                            const aiProvider = cfg?.dedupe_ai_provider || cfg?.dedup_diagnostics?.ai_provider;
                            const aiThreshold = cfg?.dedupe_ai_confidence_threshold || cfg?.dedup_diagnostics?.ai_confidence_threshold;

                            if (!mode && step.status !== "COMPLETE" && step.status !== "RUNNING") {
                              return <span className="text-muted-foreground italic">Not yet executed</span>;
                            }

                            const modeLabel = mode === "hybrid_ai_assist" ? "Hybrid (AI Assist)"
                              : mode === "ai_only" ? "AI Only"
                                : "Deterministic";

                            return (
                              <>
                                <Badge
                                  variant={mode === "deterministic" ? "outline" : "secondary"}
                                  className="gap-1 text-xs"
                                >
                                  {modeLabel}
                                </Badge>
                                {aiEnabled && aiProvider && (
                                  <span className="text-xs text-muted-foreground">
                                    AI: {aiProvider} · {((aiThreshold || 0.85) * 100).toFixed(0)}%
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* CR180: S5 Dedup Diagnostics Panel - enhanced with stage/heartbeat */}
                      {step.step_key === "S5" && step.config_json && (
                        <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                            S5 Dedup Diagnostics
                          </div>

                          {/* CR180: Stage/Heartbeat info when RUNNING */}
                          {(() => {
                            const diag = (step.config_json as any).dedup_diag;
                            if (!diag) return null;

                            const heartbeatAge = diag.heartbeat_at
                              ? Math.round((Date.now() - new Date(diag.heartbeat_at).getTime()) / 1000)
                              : null;
                            const isStale = heartbeatAge !== null && heartbeatAge > 90;

                            return (
                              <div className="text-xs font-mono bg-muted p-1 rounded space-y-1">
                                <div>stage: <span className={step.status === "RUNNING" && isStale ? "text-destructive font-bold" : ""}>{diag.stage || "?"}</span></div>
                                <div>heartbeat: {heartbeatAge !== null ? `${heartbeatAge}s ago` : "?"} {isStale && <span className="text-destructive">(STALE)</span>}</div>
                                {diag.processed_count !== undefined && <div>processed: {diag.processed_count}</div>}
                                {diag.trace_id && <div className="text-muted-foreground">trace: {diag.trace_id.slice(0, 8)}</div>}
                              </div>
                            );
                          })()}

                          {/* CR180: Input breakdown from normalized */}
                          {(() => {
                            const inputBreakdown = (step.config_json as any).input_breakdown || (step.config_json as any).dedup_diag;
                            if (!inputBreakdown?.normalized_total) return null;

                            return (
                              <div className="text-xs font-mono bg-muted/50 p-1 rounded">
                                <div>normalized: {inputBreakdown.normalized_total}, eligible: {inputBreakdown.dedup_eligible_true ?? "?"}, ineligible: {inputBreakdown.dedup_eligible_false ?? "?"}</div>
                                {Object.keys(inputBreakdown.top_blocking_policy_issues || {}).length > 0 && (
                                  <div className="text-yellow-600">top_blocks: {Object.entries(inputBreakdown.top_blocking_policy_issues).slice(0, 3).map(([k, v]) => `${k}=${v}`).join(", ")}</div>
                                )}
                              </div>
                            );
                          })()}

                          {/* Existing dedup_diagnostics grid */}
                          {(step.config_json as any).dedup_diagnostics && (
                            <>
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Normalized In:</span>{" "}
                                  <span className="font-medium">{(step.config_json as any).dedup_diagnostics.normalized_in ?? 0}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Deduped Out:</span>{" "}
                                  <span className={`font-medium ${((step.config_json as any).dedup_diagnostics.deduped_out ?? 0) === 0 ? "text-yellow-600" : "text-green-600"}`}>
                                    {(step.config_json as any).dedup_diagnostics.deduped_out ?? 0}
                                  </span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Merged Duplicates:</span>{" "}
                                  <span className="font-medium">{(step.config_json as any).dedup_diagnostics.dropped_as_duplicates ?? 0}</span>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Hard Blocked:</span>{" "}
                                  <span className={`font-medium ${((step.config_json as any).dedup_diagnostics.excluded_hard_block_count ?? 0) > 0 ? "text-destructive" : ""}`}>
                                    {(step.config_json as any).dedup_diagnostics.excluded_hard_block_count ?? 0}
                                  </span>
                                </div>
                              </div>

                              {(step.config_json as any).dedup_diagnostics.top_drop_reasons?.length > 0 && (
                                <div className="pt-2 border-t border-border/50">
                                  <span className="text-xs text-muted-foreground">Top Drop Reasons: </span>
                                  <span className="text-xs font-mono">
                                    {(step.config_json as any).dedup_diagnostics.top_drop_reasons.join(", ")}
                                  </span>
                                </div>
                              )}

                              {((step.config_json as any).dedup_diagnostics.deduped_out ?? 0) === 0 && (
                                <div className="p-2 bg-yellow-500/10 border border-yellow-500/30 rounded text-xs text-yellow-700 dark:text-yellow-300 flex items-center gap-2">
                                  <AlertCircle className="h-3 w-3" />
                                  {(step.config_json as any).terminal_why || "All normalized deals were filtered out. Review S4 output for missing fields."}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* CRxxx: Enhanced error display with debug details */}
                      <StepErrorDetails
                        errorMessage={step.error_message}
                        configJson={step.config_json}
                        stepId={step.id}
                      />

                      {(step.status === "COMPLETE" || step.records_out > 0 || (step.step_key === "S5_CROP" && snippetArtifactCount > 0) || step.step_key === "S1_SNIPPET") && onViewOutput && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => onViewOutput(step.step_key)}
                        >
                          <Eye className="h-4 w-4" />
                          {(step.step_key === "S5_CROP" || step.step_key === "S1_SNIPPET") ? "View Snippets" : "View Output"}
                        </Button>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>

        {/* CR122: Phase 4 Publish Steps (S6-S7) */}
        {futureSteps.length > 0 && (
          <Phase4PublishSteps
            batchRunId={batchRunId}
            steps={steps}
            onStepExecuted={onStepExecuted}
            onNavigateToDeduped={() => onViewOutput?.("S5")}
            sourcesEnabled={sourcesEnabled}
            onRunStep={(stepKey) => {
              const step = steps.find(s => s.step_key === stepKey);
              if (step) handleRunStep(step);
            }}
          />
        )}

      </CardContent>

      {/* Retry Dialog */}
      {retryDialogStep && (
        <RetryStepDialog
          open={!!retryDialogStep}
          onOpenChange={(open) => !open && setRetryDialogStep(null)}
          batchRunId={batchRunId}
          stepKey={retryDialogStep.step_key}
          stepName={retryDialogStep.step_name}
          onRetryStarted={onStepExecuted}
        />
      )}

      {/* CR075-P6: Render All + Continue Dialog */}
      <RenderAllAndContinueDialog
        open={renderAllDialogOpen}
        onOpenChange={setRenderAllDialogOpen}
        batchRunId={batchRunId}
        stepId={renderDialogStepId}
        pendingFlyers={pendingRenderFlyers}
        onComplete={() => {
          setRenderAllDialogOpen(false);
          onStepExecuted();
        }}
      />
    </Card>
  );
}
